/* eslint-disable react/no-unknown-property */
import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo,
} from "react";
import {
  Container,
  Row,
  Col,
  Button,
  Accordion,
  Form,
  Card,
  Modal,
  Spinner,
  Offcanvas,
  Tooltip,
  Alert,
  Badge,
  Carousel,
} from "react-bootstrap";
import {
  FaRocket,
  FaBox,
  FaCouch,
  FaCar,
  FaMapMarkerAlt,
  FaCalendarAlt,
  FaClock,
  FaChevronDown,
  FaChevronUp,
  FaCheckCircle,
  FaSmile,
  FaMeh,
  FaFrown,
  FaTimes,
  FaFilter,
  FaPaperPlane,
  FaWeightHanging,
  FaExclamationTriangle,
  FaRulerCombined,
  FaStar,
  FaStickyNote,
  FaTruck,
  FaQuestionCircle,
  FaExpand,
  FaCalendar,
  FaRoad,
  FaEuroSign,
  FaPallet,
  FaRoute,
  FaLongArrowAltRight,
  FaRegBookmark,
  FaTrash,
} from "react-icons/fa";
import { LuMessageCircleQuestion } from "react-icons/lu";
import { AiFillIdcard } from "react-icons/ai";
import { HiWrenchScrewdriver } from "react-icons/hi2";
import {
  MapContainer,
  TileLayer,
  Polyline,
  Marker,
  Popup,
} from "react-leaflet";
import "leaflet/dist/leaflet.css";
import {
  driverAPI,
  postalAreaAPI,
  authAPI,
  translateBackendMessage,
} from "../../../api/apiSystem";
import LocationAutocomplete from "../../LocationAutocomplete";
import PaginationComponent from "../../PaginationComponent";
import NavbarMenu from "../../heroSection/NavbarMenu";
import Footer from "../../footer/Footer";
import Swal from "sweetalert2";
import { useTranslation } from "react-i18next";
import "../../../assets/css/Auftraege.css";
import "./DarkTheme.css";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { RouteMap } from "../../RouteMap";
import { formatNumber, parseCoordinates } from "../../../utils/helpers";
import MapErrorBoundary from "../../MapErrorBoundary";
import DarkTransportDetailsPage from "./DarkTransportDetailsPage";
import DarkBookingDetailsPage from "./DarkBookingDetailsPage";
import { formatDisplayDate } from "../../../utils/helpers";
import { commissionLabel, isCommissionEnabled } from "../../../utils/commission";
import paletteImg from "../../../assets/images/driver_transport_icon/palette.png";
import paketeImg from "../../../assets/images/driver_transport_icon/pakete.png";
import documentImg from "../../../assets/images/driver_transport_icon/document.png";
import furnitureImg from "../../../assets/images/driver_transport_icon/furniture.png";
import electricalImg from "../../../assets/images/driver_transport_icon/electrical.png";
import tiresImg from "../../../assets/images/driver_transport_icon/tires.png";
import refrigeratorImg from "../../../assets/images/driver_transport_icon/refrigerator.png";
import buildingMaterialsImg from "../../../assets/images/driver_transport_icon/building_materials.png";
import movingImg from "../../../assets/images/driver_transport_icon/moving.png";
import bicycleImg from "../../../assets/images/driver_transport_icon/bicycle.png";
import motorcycleImg from "../../../assets/images/driver_transport_icon/motorcycle.png";
import carImg from "../../../assets/images/driver_transport_icon/car.png";
import animalTransportImg from "../../../assets/images/driver_transport_icon/animal_transport.png";
import otherImg from "../../../assets/images/driver_transport_icon/other.png";
import fastImg from "../../../assets/images/driver_transport_icon/rocket.png";
import regularImg from "../../../assets/images/driver_transport_icon/pakete.png";
import ClientChatBox from "../../chat/ClientChatBox";
import {
  AlarmIcon,
  DeliverPointIcon,
  LeavePointIcon,
  PackageIcon,
  CalendarIcon,
  ArrowIcon,
} from "../../../assets/icons";
import { useBookingEvents } from "../../useBookingEvents";
import getPusher from "../../pusher";
import PriceBar from "./PriceBar";
import DriverChatModal from "../DriverChatModal";

const DEBOUNCE_MS = 600;

const usePriceCalculator = () => {
  const [loading,     setLoading]     = useState(false);
  const [priceStatus, setPriceStatus] = useState(null); // { percentage, customerPrice } | null
  const [resData,     setResData]     = useState(null);
  const debounceTimer = useRef(null);
 
  const calculatePrice = useCallback((price, selectedId, currentType) => {
    // إلغاء الـ timer القديم لو المستخدم لسه بيكتب
    if (debounceTimer.current) clearTimeout(debounceTimer.current);
 
    if (!price || !selectedId) {
      setPriceStatus(null);
      setResData(null);
      return;
    }
 
    // ⏳ ابدأ timer جديد — هينفذ الـ API بعد 600ms من آخر تغيير
    debounceTimer.current = setTimeout(async () => {
      setLoading(true);
      setPriceStatus(null);
      setResData(null);
 
      try {
        let response;
        if (currentType === "regular") {
          response = await driverAPI.bookingsOrders.calculatePercentage({
            price,
            booking_id: selectedId,
          });
        } else if (currentType === "furnitures" || currentType === "cars") {
          response = await driverAPI.transports.calculateProfit({
            price,
            transport_id: selectedId,
          });
        }
 
        if (response?.success) {
          const data = response.data.data;
          setResData(data);
 
          if (currentType === "regular") {
            const customerPrice = parseFloat(data?.booking_total ?? 0);
            const driverPrice   = parseFloat(price);
 
            if (!customerPrice || isNaN(driverPrice)) {
              // مش قادر يحسب
              setPriceStatus({ percentage: null, customerPrice: null });
              return;
            }
 
            // ✅ الحساب الصح: (سعر السواق / سعر العميل) * 100
            const percentage = (driverPrice / customerPrice) * 100;
            setPriceStatus({ percentage, customerPrice });
          }
        }
      } catch (err) {
        console.error("❌ Price calculation error:", err);
        setPriceStatus({ percentage: null, customerPrice: null, error: true });
      } finally {
        setLoading(false);
      }
    }, DEBOUNCE_MS);
  }, []);
 
  const resetCalculator = useCallback(() => {
    if (debounceTimer.current) clearTimeout(debounceTimer.current);
    setLoading(false);
    setPriceStatus(null);
    setResData(null);
  }, []);
 
  return { loading, priceStatus, resData, calculatePrice, resetCalculator };
};

const DriverCard = () => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();

  const [showModalQuestion, setShowModalQuestion] = useState(false);

  const [userType, setUserType] = useState(
    typeof window !== "undefined" ? localStorage.getItem("userType") : null,
  );

  // 🔥 استخدام Custom Hook
  const {
    loading: priceLoading,
    priceStatus,
    resData,
    calculatePrice,
    resetCalculator,
  } = usePriceCalculator();

  const [open, setOpen] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [showPriceModal, setShowPriceModal] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [vehicles, setVehicles] = useState([]);
  const [driverVehicles, setDriverVehicles] = useState([]);
  const [data, setData] = useState([]);
  const [price, setPrice] = useState("");
  const [loading, setLoading] = useState(false);
  const [driver, setDriver] = useState(null);
  const [offCanvasOpen, setOffCanvasOpen] = useState(null);
  const [showFilterDrawer, setShowFilterDrawer] = useState(false);
  const [profileLoading, setProfileLoading] = useState(true);
  const [isApprovedByType, setIsApprovedByType] = useState(true);
  const [unreadCounts, setUnreadCounts] = useState({});
  const [showChatModal, setShowChatModal] = useState(false);
  const [selectedTransport, setSelectedTransport] = useState(null);

  // 🔥 تبسيط حالة المودال
  const [modalData, setModalData] = useState({
    booking_id: "",
    transport_id: "",
    driver_name: "",
    phone_number: "",
    vehicle_number: "",
    vehicle_type: "",
    current_location: "",
    vehicle_id: "",
    price: "",
    offer_date: "",
    info: "",
    // إضافة حقل driving_choice
    driving_choice: "", // 'drive' أو 'load'
  });

  // 🔥 تبسيط الفلاتر
  const initialFilters = {
    vehicle_type: "",
    booking_from: "",
    booking_to: "",
    date: "",
    booking_type: "",
    weight: "",
    location: "",
    radius: "",
    from_radius: "",
    to_radius: "",
  };

  const [filters, setFilters] = useState(initialFilters);
  const [tempFilters, setTempFilters] = useState(initialFilters);
  const [savedFilters, setSavedFilters] = useState([]);
  const [savedFiltersLoading, setSavedFiltersLoading] = useState(false);
  const [countData, setCountData] = useState([]);
  const [formData, setFormData] = useState({
    booking_from: "",
    booking_to: "",
    vehicle_id: "",
  });

  const [selectedId, setSelectedId] = useState(null);
  const [selectedItemForMap, setSelectedItemForMap] = useState(null);
  const selectedTransportHasCommission = isCommissionEnabled(selectedItemForMap);
  const [pagination, setPagination] = useState({
    current_page: 1,
    last_page: 1,
    per_page: 10,
    total: 0,
  });

  // SweetAlert configuration
  const showSweetAlert = (icon, title, text, confirmButtonText = "OK") => {
    return Swal.fire({
      icon,
      title,
      text,
      confirmButtonText,
      customClass: {
        confirmButton: "btn btn-primary",
      },
    });
  };
  
  const fetchChatStatistics = async (item) => {
    try {
      let transportId;
      let orderId = item.id;

      if (currentType === "furnitures" || currentType === "cars") {
        transportId = item.transport?.id;
        if (!transportId) {
          transportId = item.transport?.transport_id;
        }
      } else {
        transportId = item.booking?.id;
        if (!transportId) {
          transportId = item.booking?.booking_id;
        }
      }

      console.log("📊 Fetching chat statistics:", { transportId, orderId });

      if (!transportId || !orderId) return 0;

      const result = await driverAPI.transportOrderChat.getMessages(
        transportId,
        orderId,
      );

      console.log("📊 Chat statistics response:", result);

      if (result.success) {
        const unreadCount = result.data?.data.statistics?.unread_count;
        console.log("📊 Unread count for order", unreadCount);
        return unreadCount;
      }
    } catch (error) {
      console.error("❌ Error fetching chat statistics:", error);
    }
    return 0;
  };

  const handleMessagesRead = (orderId) => {
    setUnreadCounts((prev) => ({
      ...prev,
      [orderId]: 0,
    }));
  };

  const handleOpenChat = async (item) => {
    if (!item) {
      showErrorAlert(t("driverOffer.alerts.noItemAvailable"));
      return;
    }

    // ── Transports (cars / furnitures) ──
    if (currentType === "furnitures" || currentType === "cars") {
      const transportId = item.id || item.transport?.id || item.transport_id;

      if (!transportId) {
        showErrorAlert(t("driverOffer.alerts.transportInfoMissing"));
        return;
      }

      // افتح الشات بس — من غير إنشاء inquiry
      setSelectedTransport({
        transportId: transportId,
        orderId: null, // هيتنشأ عند أول رسالة
      });
      setShowChatModal(true);
      return;
    }

    // ── Bookings (fast / regular) ──
    if (!item.id) {
      showErrorAlert(t("driverOffer.alerts.orderIdMissing"));
      return;
    }

    const transportId = item.booking?.id || item.booking?.booking_id;
    const orderId = item.id;

    if (!transportId) {
      showErrorAlert(t("driverOffer.alerts.transportInfoMissing"));
      return;
    }

    setSelectedTransport({
      transportId: transportId,
      orderId: orderId,
    });
    setShowChatModal(true);
    handleMessagesRead(orderId);
  };

  const fetchCounts = async () => {
    setLoading(true);
    try {
      const response = await driverAPI.counts();

      if (response.success) {
        const countData = response.data.data || response.data;
        setCountData(countData);
      } else {
        Swal.fire({
          icon: "error",
          title: t("clientTable.errors.error"),
          text: response.message || t("clientTable.errors.loadFurnitureFailed"),
          confirmButtonColor: "#ff7b00",
        });
      }
    } catch (error) {
      console.error("Error loading furniture data:", error);
      Swal.fire({
        icon: "error",
        title: t("clientTable.errors.connectionError"),
        text: t("clientTable.errors.connectionFailed"),
        confirmButtonColor: "#ff7b00",
      });
    }
    setLoading(false);
  };

  useEffect(() => {
    const fetchDriver = async () => {
      setProfileLoading(true);
      try {
        const res = await authAPI.getProfile();
        if (res.success) {
          setDriver(res.data.data);
        }
      } catch (error) {
        console.error("❌ Error fetching driver profile:", error);
      } finally {
        setProfileLoading(false);
      }
    };

    fetchDriver();
    fetchCounts();
  }, []);

  useEffect(() => {
    const fetchSavedFilters = async () => {
      setSavedFiltersLoading(true);
      try {
        const response = await driverAPI.savedFilters.index();

        if (response.success && Array.isArray(response.data.data)) {
          setSavedFilters(response.data.data);
        }
      } catch (error) {
        console.error("❌ Error fetching saved filters:", error);
      } finally {
        setSavedFiltersLoading(false);
      }
    };

    fetchSavedFilters();
  }, []);

  // 🔹 تأثير لملء البيانات تلقائيًا عند تغيير أي حقل
  useEffect(() => {
    const fillDriverData = () => {
      let selectedDriver = null;

      // إذا وجدنا سائقًا، املأ باقي البيانات
      if (selectedDriver) {
        setModalData((prev) => ({
          ...prev,
          driver_name: selectedDriver.driver_name,
          phone_number: selectedDriver.phone_number,
          vehicle_number: selectedDriver.vehicle_number,
          vehicle_type: selectedDriver.vehicle_type,
          vehicle_id: selectedDriver.id,
          info: selectedDriver.info || "",
        }));
      }
    };

    fillDriverData();
  }, [
    modalData.driver_name,
    modalData.phone_number,
    modalData.vehicle_number,
    driverVehicles,
  ]);

  const shouldShowChat = (type) => {
    return type === "furnitures" || type === "cars";
  };

  const getInitialType = () => {
    const urlType = searchParams.get("type");
    return ["fast", "regular", "furnitures", "cars"].includes(urlType)
      ? urlType
      : "fast";
  };

  const cleanId = (id) => {
    if (!id) return null;
    if (typeof id === "string" && id.includes("No.")) {
      return parseInt(id.replace("No.", "").trim());
    }
    return parseInt(id);
  };

  const [currentType, setCurrentType] = useState(getInitialType());
  const [activeKey, setActiveKey] = useState(null);

  const showErrorAlert = (text) => {
    return showSweetAlert("error", t("driverCard.alerts.error"), text);
  };

  const showSuccessAlert = (text) => {
    return showSweetAlert("success", t("driverCard.alerts.success"), text);
  };

  const showWarningAlert = (text) => {
    return showSweetAlert("warning", t("driverCard.alerts.warning"), text);
  };

  const getSavedFilterTypeLabel = useCallback(
    (filterType) => {
      const translationKey = {
        fast: "driverCard.orderTypes.fast",
        regular: "driverCard.orderTypes.regular",
        furnitures: "driverCard.orderTypes.furniture",
        cars: "driverCard.orderTypes.car",
      }[filterType];

      return translationKey
        ? t(translationKey)
        : filterType || t("driverCard.savedFilters.unknownType", {
            defaultValue: "Unknown type",
          });
    },
    [t],
  );

  const getCleanFilters = useCallback((sourceFilters = {}) => {
    const cleaned = {};

    Object.entries(sourceFilters).forEach(([key, value]) => {
      if (
        value !== null &&
        value !== undefined &&
        value !== "" &&
        value !== 0
      ) {
        cleaned[key] = value;
      }
    });

    return cleaned;
  }, []);

  const buildApiFilterPayload = useCallback(
    (sourceFilters = {}) => {
      const cleaned = getCleanFilters(sourceFilters);

      const payload = {
        vehicle_type: cleaned.vehicle_type,
        from: cleaned.booking_from,
        from_radius: cleaned.from_radius,
        to: cleaned.booking_to,
        to_radius: cleaned.to_radius,
        location: cleaned.location,
        radius: cleaned.radius,
        weight: cleaned.weight,
        booking_type: cleaned.booking_type,
        date: cleaned.date,
        latitude: cleaned.location_lat,
        longitude: cleaned.location_lng,
      };

      return getCleanFilters(payload);
    },
    [getCleanFilters],
  );

  const serializeFilter = useCallback(
    (sourceFilters = {}) => JSON.stringify(getCleanFilters(sourceFilters)),
    [getCleanFilters],
  );

  const savedFiltersForDisplay = useMemo(() => {
    return [...savedFilters].sort((a, b) => {
      if (a.filter_type === currentType && b.filter_type !== currentType) {
        return -1;
      }

      if (a.filter_type !== currentType && b.filter_type === currentType) {
        return 1;
      }

      return a.name.localeCompare(b.name);
    });
  }, [savedFilters, currentType]);

  const promptToSaveFilter = useCallback(
    async (appliedFilters) => {
      const normalizedFilters = getCleanFilters(appliedFilters);

      if (Object.keys(normalizedFilters).length === 0) {
        return;
      }

      const existingFilter = savedFilters.find(
        (savedFilter) =>
          savedFilter.filter_type === currentType &&
          serializeFilter(savedFilter.filters) ===
            serializeFilter(normalizedFilters),
      );

      if (existingFilter) {
        await showWarningAlert(
          t("This filter has already been saved", {
            defaultValue: "This filter has already been saved",
          }),
        );
        return;
      }

      const savePrompt = await Swal.fire({
        icon: "question",
        title: t("driverCard.savedFilters.savePromptTitle", {
          defaultValue: "Save this filter?",
        }),
        text: t("driverCard.savedFilters.savePromptText", {
          defaultValue:
            "Do you want to save this filter so you can use it again later?",
        }),
        showCancelButton: true,
        confirmButtonText: t("driverCard.savedFilters.saveNow", {
          defaultValue: "Save filter",
        }),
        cancelButtonText: t("driverCard.buttons.cancel"),
      });

      if (!savePrompt.isConfirmed) {
        return;
      }

      const namePrompt = await Swal.fire({
        title: t("driverCard.savedFilters.namePromptTitle", {
          defaultValue: "Filter name",
        }),
        input: "text",
        inputLabel: t("driverCard.savedFilters.namePromptLabel", {
          defaultValue: "Enter a name for this saved filter",
        }),
        inputPlaceholder: t("driverCard.savedFilters.namePlaceholder", {
          defaultValue: "For example: Berlin morning jobs",
        }),
        showCancelButton: true,
        confirmButtonText: t("driverCard.savedFilters.confirmName", {
          defaultValue: "Save",
        }),
        cancelButtonText: t("driverCard.buttons.cancel"),
        inputValidator: (value) => {
          const normalizedValue = value?.trim();

          if (!normalizedValue) {
            return t("driverCard.savedFilters.nameRequired", {
              defaultValue: "Filter name is required",
            });
          }

          const duplicateName = savedFilters.some(
            (savedFilter) =>
              savedFilter.name?.trim()?.toLowerCase() ===
              normalizedValue.toLowerCase(),
          );

          if (duplicateName) {
            return t("A saved filter with this name already exists", {
              defaultValue: "A saved filter with this name already exists",
            });
          }

          return undefined;
        },
      });

      if (!namePrompt.isConfirmed || !namePrompt.value?.trim()) {
        return;
      }

      try {
        const response = await driverAPI.savedFilters.store({
          name: namePrompt.value.trim(),
          filter_type: currentType,
          filters: normalizedFilters,
        });

        if (!response?.success) {
          throw new Error(
            translateBackendMessage(
              response,
              t("driverCard.savedFilters.savedError", {
                defaultValue: "Unable to save filter",
              }),
            ),
          );
        }

        const savedFilter = response?.data?.data;

        if (savedFilter) {
          setSavedFilters((prev) => [savedFilter, ...prev]);
        }

        await showSuccessAlert(
          translateBackendMessage(
            response?.message,
            t("driverCard.savedFilters.savedSuccess", {
              defaultValue: "Filter saved successfully",
            }),
          ),
        );
      } catch (error) {
        console.error("❌ Error saving filter:", error);
        await showErrorAlert(
          translateBackendMessage(
            error?.response?.data || error?.message,
            t("driverCard.savedFilters.savedError", {
              defaultValue: "Unable to save filter",
            }),
          ),
        );
      }
    },
    [
      currentType,
      getCleanFilters,
      savedFilters,
      serializeFilter,
      showErrorAlert,
      showSuccessAlert,
      showWarningAlert,
      t,
    ],
  );

  const handleApplySavedFilter = useCallback(
    (savedFilter) => {
      if (!savedFilter) {
        return;
      }

      const nextFilters = getCleanFilters(savedFilter.filters || {});
      const selectedVehicle = vehicles.find(
        (vehicle) => vehicle.name === nextFilters.vehicle_type,
      );

      setTempFilters(nextFilters);
      setFilters(nextFilters);
      setCurrentType(savedFilter.filter_type || currentType);
      setFormData((prev) => ({
        ...prev,
        vehicle_id: selectedVehicle ? String(selectedVehicle.id) : "",
      }));
      setShowFilterDrawer(false);
    },
    [currentType, getCleanFilters, vehicles],
  );

  const handleDeleteSavedFilter = useCallback(
    async (savedFilter) => {
      if (!savedFilter?.id) {
        return;
      }

      const confirmation = await Swal.fire({
        icon: "warning",
        title: t("driverCard.savedFilters.deleteTitle", {
          defaultValue: "Delete saved filter?",
        }),
        text: t("driverCard.savedFilters.deleteText", {
          defaultValue: "This saved filter will be removed permanently.",
        }),
        showCancelButton: true,
        confirmButtonText: t("driverCard.savedFilters.deleteConfirm", {
          defaultValue: "Delete",
        }),
        cancelButtonText: t("driverCard.buttons.cancel"),
      });

      if (!confirmation.isConfirmed) {
        return;
      }

      try {
        const response = await driverAPI.savedFilters.delete(savedFilter.id);

        if (!response?.success) {
          throw new Error(
            translateBackendMessage(
              response,
              t("driverCard.savedFilters.deleteError", {
                defaultValue: "Unable to delete filter",
              }),
            ),
          );
        }

        setSavedFilters((prev) =>
          prev.filter((item) => item.id !== savedFilter.id),
        );

        await showSuccessAlert(
          translateBackendMessage(
            response?.message,
            t("driverCard.savedFilters.deleteSuccess", {
              defaultValue: "Filter deleted successfully",
            }),
          ),
        );
      } catch (error) {
        console.error("❌ Error deleting filter:", error);
        await showErrorAlert(
          translateBackendMessage(
            error?.response?.data || error?.message,
            t("driverCard.savedFilters.deleteError", {
              defaultValue: "Unable to delete filter",
            }),
          ),
        );
      }
    },
    [showErrorAlert, showSuccessAlert, t],
  );

  // جلب سيارات السائق
  useEffect(() => {
    const fetchDriverVehicles = async () => {
      try {
        const res = await driverAPI.vehicles.index();
        if (res.success && Array.isArray(res.data.data)) {
          setDriverVehicles(res.data.data);
          console.log("🚗 Driver vehicles loaded:", res.data.data);
        } else {
          console.warn("⚠️ Unexpected driver vehicles response:", res);
        }
      } catch (err) {
        console.error("❌ Error fetching driver vehicles:", err);
      }
    };
    fetchDriverVehicles();
  }, []);

  const handleCloseModal = useCallback(() => {
    setShowModal(false);
    setModalData({
      booking_id: "",
      transport_id: "",
      driver_name: "",
      phone_number: "",
      vehicle_number: "",
      vehicle_type: "",
      current_location: "",
      vehicle_id: "",
      price: "",
      offer_date: "",
      info: "",
      driving_choice: "",
    });
    setPrice("");
    setSelectedId(null); // إضافة مسح selectedId
    resetCalculator(); // مسح بيانات السعر
  }, [resetCalculator]);

  const generateDetailsUrl = (booking) => {
    let type;

    if (booking.transport_type) {
      if (booking.transport_type === "car") {
        type = "cars";
      } else {
        type = "furnitures";
      }
    } else if (booking.fast_order === 1) {
      type = "fast";
    } else {
      type = "regular";
    }

    const param =
      type === "cars" || type === "furnitures" ? "transport_id" : "booking_id";
    const id = booking.booking_id || booking.id;

    console.log("🔗 Generated URL:", {
      id,
      type,
      param,
      transportType: booking.transport_type,
      fastOrder: booking.fast_order,
      itemTypes: booking.item_type,
    });

    return `/driver/driver-offer?${param}=${id}&type=${type}&open_details=true`;
  };

  /** بناء نص رسالة العرض في الشات */
  const PAYMENT_TERM_LABELS = {
    on_delivery: "Bei Lieferung",
    "3_days": "3 Tage",
    "7_days": "7 Tage",
    "14_days": "14 Tage",
    "30_days": "30 Tage",
    by_agreement: "Nach Vereinbarung",
  };

  /** بناء رسالة العرض كـ JSON عشان الشات يعرضها Card */
  const buildOfferChatMessage = (item, offerPayload = {}) => {
    const fromRaw = item?.booking_from ?? item?.transport?.booking_from ?? null;
    const toRaw = item?.booking_to ?? item?.transport?.booking_to ?? null;

    const from = Array.isArray(fromRaw) ? fromRaw[0] : fromRaw;
    const to = Array.isArray(toRaw) ? toRaw[0] : toRaw;

    const paymentTermKey =
      item?.payment_term ?? item?.transport?.payment_term ?? "";
    const paymentTermLabel =
      PAYMENT_TERM_LABELS[paymentTermKey] || paymentTermKey || "";

    const payload = {
      type: "offer_card",
      status: "pending",
      from: from || "N/A",
      to: to || "N/A",
      price: String(offerPayload.price ?? "-"),
      currency: "EUR",
      payment_term: paymentTermLabel,
      valid_until: offerPayload.offer_date || "",
    };

    return `__OFFER_CARD__${JSON.stringify(payload)}`;
  };

  /** إرسال رسالة العرض بعد نجاح store — orderId من الـ response */
  const sendOfferChatMessage = async (transportId, orderId, item, offerPayload) => {
    const cleanTransportId = cleanId(transportId);
    const cleanOrderId = cleanId(orderId);

    if (!cleanTransportId || !cleanOrderId) {
      console.warn("⚠️ Missing IDs for offer chat message", {
        transportId,
        orderId,
      });
      return;
    }

    try {
      const formData = new FormData();
      formData.append("message", buildOfferChatMessage(item, offerPayload));

      const result = await driverAPI.transportOrderChat.sendMessage(
        cleanTransportId,
        cleanOrderId,
        formData,
      );

      if (!result?.success) {
        console.error("❌ Offer chat message failed:", result?.message);
      }
    } catch (error) {
      console.error("❌ Error sending offer chat message:", error);
    }
  };

  const handleSendOffer = async () => {
    if (isSubmitting) return;

    try {
      setIsSubmitting(true);

      // ── تأكيد حسب النوع ──
      let confirmationResult;

      if (currentType === "fast") {
        confirmationResult = await Swal.fire({
          title: t("driverCard.alerts.confirmFastOrderTitle"),
          text: t("driverCard.alerts.confirmFastOrderText"),
          icon: "question",
          showCancelButton: true,
          confirmButtonText: t("driverCard.buttons.orderTake"),
          cancelButtonText: t("driverCard.buttons.cancel"),
          confirmButtonColor: "#3085d6",
          cancelButtonColor: "#d33",
          allowOutsideClick: () => !isSubmitting,
        });
      } else if (
        (currentType === "furnitures" || currentType === "cars") &&
        selectedTransportHasCommission
      ) {
        confirmationResult = await Swal.fire({
          title: t("driverCard.alerts.confirmTransportOfferTitle"),
          html: `
            <div class="text-start">
              <p>${t("driverCard.alerts.confirmTransportOfferText")}</p>
              <strong>${t("driverCard.alerts.commissionText")}: ${
                resData?.platform_commission || "0,00"
              } €</strong><br/>
            </div>
          `,
          icon: "question",
          showCancelButton: true,
          confirmButtonText: t("driverCard.buttons.yesSend"),
          cancelButtonText: t("driverCard.buttons.cancel"),
          confirmButtonColor: "#3085d6",
          cancelButtonColor: "#d33",
          customClass: { popup: "custom-swal-popup" },
          allowOutsideClick: () => !isSubmitting,
        });
      } else {
        confirmationResult = await Swal.fire({
          title: t("driverCard.alerts.confirmTransportOfferTitle"),
          text: t("driverCard.alerts.confirmTransportOfferText"),
          icon: "question",
          showCancelButton: true,
          confirmButtonText: t("driverCard.buttons.yesSend"),
          cancelButtonText: t("driverCard.buttons.cancel"),
          confirmButtonColor: "#3085d6",
          cancelButtonColor: "#d33",
          allowOutsideClick: () => !isSubmitting,
        });
      }

      if (!confirmationResult.isConfirmed) {
        return;
      }

      // ── Validation ──
      if (
        !modalData.driver_name ||
        !modalData.phone_number ||
        !modalData.vehicle_type
      ) {
        showErrorAlert(t("driverCard.alerts.requiredFieldsMissing"));
        return;
      }

      const hasDrivingOption =
        currentType === "cars" &&
        selectedItemForMap?.driving?.some((drive) => drive === "1");

      if (hasDrivingOption && !modalData.driving_choice) {
        showErrorAlert(t("driverCard.alerts.pleaseSelectDrivingOption"));
        return;
      }

      if (
        (currentType === "regular" ||
          currentType === "furnitures" ||
          currentType === "cars") &&
        !modalData.price
      ) {
        showErrorAlert(t("driverCard.alerts.priceRequired"));
        return;
      }

      // ── Payload أساسي ──
      const baseData = {
        driver_name: modalData.driver_name.trim(),
        phone_number: modalData.phone_number.trim(),
        vehicle_type: modalData.vehicle_type,
        current_location: Array.isArray(modalData.current_location)
          ? modalData.current_location.join(", ")
          : modalData.current_location?.trim() || "",
        info: modalData.info?.trim() || "",
      };

      if (hasDrivingOption) {
        baseData.driving_choice = modalData.driving_choice;
        baseData.vehicle_number =
          modalData.driving_choice === "drive"
            ? modalData.vehicle_number?.trim() || ""
            : null;
      } else {
        baseData.vehicle_number = modalData.vehicle_number?.trim() || "";
      }

      // ── إرسال حسب النوع ──
      let res;

      if (currentType === "fast") {
        res = await driverAPI.fastOrders.store({
          booking_id: modalData.booking_id,
          ...baseData,
          vehicle_id: modalData.vehicle_id,
        });
      } else if (currentType === "regular") {
        res = await driverAPI.bookingsOrders.store({
          booking_id: modalData.booking_id,
          ...baseData,
          vehicle_id: modalData.vehicle_id,
          price: parseFloat(modalData.price),
        });
      } else if (currentType === "furnitures" || currentType === "cars") {
        const transportData = {
          transport_id: modalData.transport_id,
          ...baseData,
          price: parseFloat(modalData.price),
        };

        if (modalData.offer_date) {
          transportData.offer_date = modalData.offer_date;
        }

        res = await driverAPI.transports.store(transportData);
      }

      if (!res?.success) {
        throw new Error(res?.message || t("driverCard.alerts.offerSentError"));
      }

      // ── رسالة شات تلقائية (transport فقط) ──
      if (currentType === "furnitures" || currentType === "cars") {
        const order = res.data?.data ?? res.data ?? {};
        const orderId = order.id;
        const transportId = modalData.transport_id;

        const item =
          selectedItemForMap ||
          data.find((d) => String(d.id) === String(transportId));

        if (orderId && transportId && item) {
          await sendOfferChatMessage(transportId, orderId, item, {
            price: parseFloat(modalData.price),
            offer_date: modalData.offer_date || "",
          });
        }
      }

      // ── نجاح ──
      setShowModal(false);
      await showSuccessAlert(t("driverCard.alerts.offerSentSuccess"));
      handleCloseModal();
      await handleFetch();
      navigate("/driver/driver-offer");
    } catch (err) {
      console.error("❌ Error in handleSendOffer:", err);
      await showErrorAlert(
        translateBackendMessage(
          err?.response?.data || err,
          t("driverCard.alerts.generalError"),
        ),
      );
    } finally {
      setIsSubmitting(false);
    }
  };

/** إرسال رسالة العرض في الشات بعد إنشاء/تحديث الـ order */
const sendOfferToChat = async (transportId, orderId, item, offerPayload) => {
  if (!transportId || !orderId) return;

  const message = buildOfferChatMessage(item, offerPayload);
  const formData = new FormData();
  formData.append("message", message);

  try {
    await driverAPI.transportOrderChat.sendMessage(
      transportId,
      orderId,
      formData,
    );
  } catch (err) {
    console.error("❌ Failed to send offer chat message:", err);
    // العرض اتعمل بنجاح — الرسالة فشل مش لازم يوقف الـ UX
  }
};

  const handlePriceChange = useCallback(
    (e, context = "modal") => {
      const newPrice = Number(e.target.value);
      setPrice(newPrice);

      if (context === "modal") {
        setModalData((prev) => ({ ...prev, price: newPrice }));
      }

      if (newPrice === "" || !selectedId) {
        resetCalculator();
        return;
      }

      // 🔥 حساب السعر مباشرة
      calculatePrice(newPrice, selectedId, currentType);
    },
    [
      selectedId,
      currentType,
      calculatePrice,
      resetCalculator,
    ],
  );

  // 🔥 تأثير لحساب السعر تلقائيًا عند فتح مودال العرض لجميع الأنواع
  useEffect(() => {
    if (showModal) {
      // استخدام الـ ID الصحيح بناءً على النوع
      const idToUse =
        currentType === "furnitures" || currentType === "cars"
          ? modalData.transport_id
          : modalData.booking_id;

      if (idToUse) {
        console.log("🔄 Setting selectedId for:", currentType, "ID:", idToUse);
        setSelectedId(idToUse);

        // إذا كان هناك سعر موجود، احسب تلقائيًا
        if (modalData.price) {
          console.log(
            "🔄 Auto-calculating price for:",
            idToUse,
            "Price:",
            modalData.price,
          );
          calculatePrice(modalData.price, idToUse, currentType);
        }
      }
    }
  }, [
    showModal,
    modalData.transport_id,
    modalData.booking_id,
    modalData.price,
    currentType,
    calculatePrice,
  ]);

  useEffect(() => {
    const fetchVehicles = async () => {
      try {
        const res = await postalAreaAPI.index();
        if (res.success && Array.isArray(res.data?.data)) {
          setVehicles(res.data.data);
        } else {
          console.warn("⚠️ Unexpected postal area response:", res);
        }
      } catch (err) {
        console.error("❌ Error fetching vehicles:", err);
      }
    };
    fetchVehicles();
  }, []);

  // 🔥 تعديل دالة handleFetch لترسل فقط الفلاتر التي تحتوي على قيم
  const handleFetch = async (page = 1, filterOverride = null) => {
    console.log("🚀 ~ handleFetch ~ page:", page);
    setLoading(true);
    try {
      const params = {
        page,
        per_page: pagination.per_page,
      };

      const activeFilters = filterOverride || filters;
      const filterPayload = buildApiFilterPayload(activeFilters);

      console.log("📡 Fetching data for type:", currentType);
      console.log("🔍 Active filters:", filterPayload);

      let response;

      if (currentType === "regular") {
        response = await driverAPI.bookings.regular({
          ...filterPayload,
          ...params,
        });
      } else if (currentType === "fast") {
        response = await driverAPI.bookings.fast({
          ...filterPayload,
          ...params,
        });
      } else if (currentType === "cars") {
        response = await driverAPI.transports.cars({
          ...filterPayload,
          ...params,
        });
      } else if (currentType === "furnitures") {
        response = await driverAPI.transports.furnitures({
          ...filterPayload,
          ...params,
        });
      } else {
        throw new Error(`Invalid currentType: ${currentType}`);
      }

      if (response && response.success) {
        // Capture the is_approved status from the top level of the response
        if (response.data.is_approved !== undefined) {
          setIsApprovedByType(response.data.is_approved);
        } else {
          setIsApprovedByType(true); // Default to true if not provided (fallback)
        }

        const fetchedData = Array.isArray(response.data.data)
          ? response.data.data
          : [];
        setData(fetchedData);

        setPagination(
          response.data.meta || {
            current_page: page,
            last_page: 1,
            per_page: 10,
            total: response.data.data?.length || 0,
          },
        );

        console.log(
          "📦 Fetched data count:",
          fetchedData.length,
          "for type:",
          currentType,
        );

        // التحقق من وجود طلب مطلوب فتحه
        const bookingId = searchParams.get("booking_id");
        const transportId = searchParams.get("transport_id");
        const openDetails = searchParams.get("open_details");
        const urlType = searchParams.get("type");

        // فقط افتح التفاصيل إذا كان الـ type متطابق
        if (
          openDetails === "true" &&
          urlType === currentType &&
          fetchedData.length > 0
        ) {
          setTimeout(() => {
            openItemDetails(bookingId, transportId);
          }, 500);
        }
      } else {
        console.error("API Error:", response?.message || "No response");
        setData([]);
        showErrorAlert(
          response?.message || t("driverCard.alerts.fetchDataError"),
        );
      }
    } catch (error) {
      console.error(error);
      setData([]);
      showErrorAlert(t("driverCard.alerts.fetchDataError"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!["fast", "regular", "furnitures", "cars"].includes(currentType))
      return;
    const filterPayload = buildApiFilterPayload(filters);
    console.log("🎯 Applying filters:", filterPayload);
    handleFetch(1, filterPayload);
  }, [filters, currentType, buildApiFilterPayload]);

  // 🔥 تعديل دالة handleApplyFilters لتطبيق الفلاتر المؤقتة
  const handleApplyFilters = async () => {
    const cleanFilters = getCleanFilters(tempFilters);

    console.log("✅ Applying clean filters:", cleanFilters);
    setFilters(cleanFilters);
    setShowFilterDrawer(false);
    await promptToSaveFilter(cleanFilters);
  };

  const renderMobileCard = (item, index) => {
    const pickupDateTime = getDateTime(currentType, item, true);
    const deliveryDateTime = getDateTime(currentType, item, false);

    return (
      <div key={index} className="dark-mobile-card">
        {/* Header: عرض نوع الشحن وعدده */}
        <div className="dark-mobile-card-header">
          <div className="dark-mobile-artikel-title">
            <TransportImage
              type={currentType}
              item={item}
              className="dark-mobile-transport-img"
              size={28}
            />
            <span>{getTypeText(currentType, item)}</span>
          </div>
        </div>

        {/* Body */}
        <div className="dark-mobile-card-body">
          {/* Location Info */}
          <div className="dark-mobile-location-row">
            {/* From Locations */}
            {(Array.isArray(item.booking_from) && item.booking_from.length > 0
              // ? item.booking_from
              // : [item.booking_from].filter(Boolean)
              ? [item.booking_from[0]]
              : [item.booking_from].filter(Boolean)
            ).map((location, i) => {
              const rawDt = item.loading_date_start
                ? null
                : (Array.isArray(item.loading_date_time)
                  ? item.loading_date_time[0]
                  : null) || "";
              const spaceIdx = rawDt ? rawDt.indexOf(" ") : -1;
              const dtDate =
                item.loading_date_start ||
                item.loading_date ||
                (spaceIdx > -1 ? rawDt.substring(0, spaceIdx) : rawDt) ||
                "-";
              const dtTime =
                item.loading_time_start ||
                item.loading_time ||
                (spaceIdx > -1
                  ? rawDt.substring(spaceIdx + 1).replace(" - ", " → ")
                  : "-");
              return (
                <div key={`pickup-${i}`} className="dark-mobile-location-item">
                  <div className="address">
                    <FaMapMarkerAlt />
                    <span>{location}</span>
                  </div>
                  <div className="datetime">
                    <span className="date-line">
                      <FaCalendarAlt />
                      {dtDate}
                      {item.loading_date_end
                        ? ` → ${item.loading_date_end}`
                        : ""}
                    </span>
                    <span className="date-line">
                      <AlarmIcon style={{ width: 14 }} />
                      {dtTime}
                      {item.loading_time_end
                        ? ` → ${item.loading_time_end}`
                        : ""}
                    </span>
                  </div>
                </div>
              );
            })}

            {/* adding fire  */}
            {/* To Locations */}
            {(Array.isArray(item.booking_to) && item.booking_to.length > 0
              // ? item.booking_to
              // : [item.booking_to].filter(Boolean)
              ? [item.booking_to[0]]
              : [item.booking_to].filter(Boolean)
            ).map((location, i) => {
              const rawDt = item.unloading_date_start
                ? null
                : (Array.isArray(item.unloading_date_time)
                  ? item.unloading_date_time[0]
                  : null) || "";
              const spaceIdx = rawDt ? rawDt.indexOf(" ") : -1;
              const dtDate =
                item.unloading_date_start ||
                item.unloading_date ||
                (spaceIdx > -1 ? rawDt.substring(0, spaceIdx) : rawDt) ||
                "-";
              const dtTime =
                item.unloading_time_start ||
                item.unloading_time ||
                (spaceIdx > -1
                  ? rawDt.substring(spaceIdx + 1).replace(" - ", " → ")
                  : "-");
              return (
                <div
                  key={`delivery-${i}`}
                  className="dark-mobile-location-item"
                >
                  <div className="address">
                    <FaMapMarkerAlt className="to-icon" />
                    <span>{location}</span>
                  </div>
                  <div className="datetime">
                    <span className="date-line">
                      <FaCalendarAlt />
                      {dtDate}
                      {item.unloading_date_end
                        ? ` → ${item.unloading_date_end}`
                        : ""}
                    </span>
                    <span className="date-line">
                      <AlarmIcon style={{ width: 14 }} />
                      {dtTime}
                      {item.unloading_time_end
                        ? ` → ${item.unloading_time_end}`
                        : ""}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Stats Row */}
          <div className="dark-mobile-stats-row">
            <div className="dark-mobile-stat-item">
              <span className="stat-label">Route:</span>
              <span className="stat-value">{(item.distance || "0 km").replace("km", " km")}</span>
            </div>
            <div className="dark-mobile-stat-item">
              <span className="stat-label">Stops:</span>
              <span className="stat-value">{(Array.isArray(item.booking_from) ? item.booking_from.length : 1)}</span>
            </div>
            <div className="dark-mobile-stat-item">
              <span className="stat-label">Gewicht:</span>
              <span className="stat-value">{item.total_weight || "0"} Kg</span>
            </div>
          </div>

          {/* Footer: Price & Button */}
          <div className="dark-mobile-footer">
            {item.discounted_price && (
              <div className="dark-mobile-price">
                <span className="price-value">
                  {item.discounted_price || item.price || "0"} EUR
                </span>
                <span className="price-label">(Netto)</span>
              </div>
            )}

            {item.price_offer !== 0 && item.price_offer && (
              <div className="dark-mobile-price">
                <span className="price-value">
                  {item.price_offer || item.price || "0"} EUR
                </span>
                {item?.is_price_negotiable !== 0 && (
                  <span className="price-label">({t("delivery_summary.priceOffer.negotiable")})</span>
                )} 
              </div>
            )}

            <button
              className="dark-mobile-details-btn"
              onClick={() => toggleOffCanvas(index)}
            >
              {t("driverCard.buttons.details")}
              <FaChevronDown />
            </button>
          </div>
        </div>
      </div>
    );
  };

  // 🔥 تعديل دالة handleClearFilters لمسح كل الفلاتر
  const handleClearFilters = () => {
    const clearedFilters = {
      vehicle_type: "",
      booking_from: "",
      booking_to: "",
      date: "",
      booking_type: "",
      weight: "",
      location: "",
      radius: "",
      from_radius: "",
      to_radius: "",
    };
    setTempFilters(clearedFilters);
    setFilters(clearedFilters); // هذا سيتسبب في إعادة جلب البيانات بدون فلاتر
    setFormData({
      booking_from: "",
      booking_to: "",
      vehicle_id: "",
    });
  };

  // 🔥 تعديل دالة handleTempFilterChange للتعامل مع القيم الفارغة
  const handleTempFilterChange = (e) => {
    const { name, value } = e.target;

    // إذا كانت القيمة فارغة، احذف الحقل من tempFilters
    if (value === "" || value === null || value === undefined || value === 0) {
      setTempFilters((prev) => {
        const newFilters = { ...prev };
        delete newFilters[name]; // حذف الحقل الفارغ
        return newFilters;
      });
    } else {
      setTempFilters((prev) => ({ ...prev, [name]: value }));
    }
  };

  // 🔥 تعديل دالة handleTempLocationChange للتعامل مع القيم الفارغة
  const handleTempLocationChange = (payload) => {
    let name, value, lat, lng;
    if (payload && payload.target) {
      name = payload.target.name;
      value = payload.target.value;
      lat = payload.lat;
      lng = payload.lng;
    } else {
      name = payload?.name;
      value = payload?.value ?? payload?.label ?? "";
      lat = payload?.lat;
      lng = payload?.lng;
    }

    if (!name) return;

    // إذا كانت القيمة فارغة، احذف الحقول المرتبطة
    if (value === "" || value === null || value === undefined) {
      setTempFilters((prev) => {
        const newFilters = { ...prev };
        delete newFilters[name];
        delete newFilters[`${name}_lat`];
        delete newFilters[`${name}_lng`];
        return newFilters;
      });
    } else {
      setTempFilters((prev) => ({
        ...prev,
        [name]: value,
        ...(lat && { [`${name}_lat`]: lat }),
        ...(lng && { [`${name}_lng`]: lng }),
      }));
    }
  };

  // تحديث دالة handleLocationChange الخاصة بالـ modal
  const handleModalLocationChange = (payload) => {
    console.log("📍 Modal Location Payload:", payload);

    let name, value, lat, lng;

    if (payload && payload.target) {
      // إذا كان من Form.Control العادي
      name = payload.target.name;
      value = payload.target.value;
      lat = payload.lat;
      lng = payload.lng;
    } else if (payload && payload.value !== undefined) {
      // إذا كان من LocationAutocomplete
      name = payload.name || "current_location";
      value = payload.value || payload.label || "";
      lat = payload.lat;
      lng = payload.lng;
    } else {
      return;
    }

    setModalData((prev) => ({
      ...prev,
      [name]: value,
      ...(lat && { [`${name}_lat`]: lat }),
      ...(lng && { [`${name}_lng`]: lng }),
    }));
  };

  // فتح دراور الفلاتر
  const handleOpenFilterDrawer = () => {
    setTempFilters({ ...filters });
    const selectedVehicle = vehicles.find(
      (vehicle) => vehicle.name === filters.vehicle_type,
    );
    setFormData((prev) => ({
      ...prev,
      vehicle_id: selectedVehicle ? String(selectedVehicle.id) : "",
    }));
    setShowFilterDrawer(true);
  };

  // إغلاق الدراور بدون تطبيق التغييرات
  const handleCloseFilterDrawer = () => {
    setShowFilterDrawer(false);
  };

  useEffect(() => {
    const bookingId = searchParams.get("booking_id");
    const transportId = searchParams.get("transport_id");
    const type = searchParams.get("type");
    const openDetails = searchParams.get("open_details");

    console.log("🔍 URL Parameters:", {
      bookingId,
      transportId,
      type,
      openDetails,
    });

    if (type && ["fast", "regular", "furnitures", "cars"].includes(type)) {
      // تحديث الـ type أولاً إذا كان مختلفاً
      if (type !== currentType) {
        console.log("🔄 Updating current type from:", currentType, "to:", type);
        setCurrentType(type);
        // لا نفتح التفاصيل هنا - سننتظر حتى يتم تحديث الـ type وتحميل البيانات
        return;
      }
    }

    // إذا كان الـ type صحيحاً وهناك طلب لفتح التفاصيل
    if (openDetails === "true" && data.length > 0) {
      openItemDetails(bookingId, transportId);
    }
  }, [searchParams, data, currentType]);

  // 🔥 تأثير منفصل لمعالجة تغيير الـ type
  useEffect(() => {
    const openDetails = searchParams.get("open_details");
    const bookingId = searchParams.get("booking_id");
    const transportId = searchParams.get("transport_id");

    if (openDetails === "true" && data.length > 0) {
      // إذا تم تغيير الـ type وكانت البيانات محملة، افتح التفاصيل
      openItemDetails(bookingId, transportId);
    }
  }, [currentType, data]);

  // 🔥 وظيفة مساعدة لفتح تفاصيل العنصر
  const openItemDetails = (bookingId, transportId) => {
    const targetId = bookingId || transportId;
    console.log(
      "🎯 Looking for item with ID:",
      targetId,
      "in data of length:",
      data.length,
    );

    const targetIndex = data.findIndex((item) => {
      const itemId = String(item.id);
      const itemBookingId = String(item.booking_id);
      const targetIdStr = String(targetId);

      return itemId === targetIdStr || itemBookingId === targetIdStr;
    });

    console.log("🔍 Found target index:", targetIndex);

    if (targetIndex !== -1) {
      // فتح الـ Accordion في عرض desktop
      setActiveKey(targetIndex.toString());
      console.log("✅ Opened accordion for index:", targetIndex);

      // فتح الـ Offcanvas في عرض mobile
      setOffCanvasOpen(targetIndex);
      console.log("✅ Opened offcanvas for index:", targetIndex);

      // إزالة параметر open_details من الـ URL بعد فتح التفاصيل
      setTimeout(() => {
        const newSearchParams = new URLSearchParams(searchParams);
        newSearchParams.delete("open_details");
        navigate(`${location.pathname}?${newSearchParams.toString()}`, {
          replace: true,
        });
      }, 1000);
    }
  };

  // --------------- Puser Booking -----------
  const handleNewBooking = useCallback(
    (newBooking) => {
      console.log("🎉 New booking received via Pusher:", newBooking);

      setData((prevData) => {
        const exists = prevData.some((item) => item.id === newBooking.id);
        if (exists) {
          console.log("⚠️ Booking already exists in list");
          return prevData;
        }

        const updatedData = [newBooking, ...prevData];
        console.log(`✅ Added new booking. Total: ${updatedData.length}`);
        return updatedData;
      });

      Swal.fire({
        icon: "success",
        title: t("driverCard.alerts.newBooking"),
        html: `
        <div class="text-start">
          <p><strong>${t("driverCard.alerts.newBookingAvailable")}</strong></p>
        </div>
      `,
        toast: true,
        position: "top-end",
        showConfirmButton: false,
        confirmButtonText: t("driverCard.buttons.view"),
        timer: 8000,
        timerProgressBar: true,
        didOpen: (toast) => {
          toast.addEventListener("click", () => {
            openItemDetails(newBooking.id, null);
          });
        },
      }).then((result) => {
        if (result.isConfirmed) {
          openItemDetails(newBooking.id, null);
        }
      });

      // play sound
      try {
        const audio = new Audio("./notification.mp3");
        audio.play().catch((e) => console.log("Audio play failed:", e));
      } catch (error) {
        console.log("Audio not supported");
      }
    },
    [t, openItemDetails],
  );

  const { isConnected, lastEvent } = useBookingEvents(
    currentType,
    handleNewBooking,
  );

  useEffect(() => {
    if (isConnected) {
      console.log("🟢 Pusher connected - receiving live updates");
    } else {
      console.log("🔴 Pusher disconnected - no live updates");
    }
  }, [isConnected]);
  // __________________________________________

  const handleVehicleChange = (e) => {
    const value = e.target.value;
    setFormData((prev) => ({ ...prev, vehicle_id: value }));
    const found = vehicles.find((v) => String(v.id) === String(value));
    const vehicleTypeValue = found?.name || "";

    setTempFilters((prev) => {
      if (!vehicleTypeValue) {
        const nextFilters = { ...prev };
        delete nextFilters.vehicle_type;
        return nextFilters;
      }

      return {
        ...prev,
        vehicle_type: vehicleTypeValue,
      };
    });
  };

  // 🔥 وظيفة تغيير الـ type
  const handleTypeChange = (newType) => {
    console.log("🔄 Changing type from:", currentType, "to:", newType);
    setCurrentType(newType);
    setActiveKey(null); // إغلاق أي تفاصيل مفتوحة
    setOffCanvasOpen(null);

    // مسح الـ URL parameters عند تغيير الـ tab
    const newSearchParams = new URLSearchParams();
    navigate(`${location.pathname}?${newSearchParams.toString()}`, {
      replace: true,
    });
  };

  const createRoute = (fromCoords, toCoords) => {
    if (!fromCoords || !toCoords) return [];
    return [fromCoords, toCoords];
  };

  const transportImages = {
    palette: paletteImg,
    pakete: paketeImg,
    document: documentImg,
    furniture: furnitureImg,
    electrical: electricalImg,
    tires: tiresImg,
    refrigerator: refrigeratorImg,
    "building materials": buildingMaterialsImg,
    moving: movingImg,
    bicycle: bicycleImg,
    motorcycle: motorcycleImg,
    car: carImg,
    "animal transport": animalTransportImg,
    other: otherImg,
    fast: fastImg,
    regular: regularImg,
    furnitures: furnitureImg,
    cars: carImg,
  };

  const TransportImage = ({ type, item, className = "", size = 180 }) => {
    const getImageSrc = () => {
      // 1. Try shipment_data (for regular/fast orders)
      const shipmentData = item.shipment_data || item.booking?.shipment_data;
      if (shipmentData && shipmentData.length > 0) {
        const firstShipment = shipmentData[0].shipment_data?.toLowerCase();
        if (firstShipment) {
          // Normalization: German terms -> English keys
          let normalizedKey = firstShipment === "pallete" ? "palette" : firstShipment;
          normalizedKey = normalizedKey === "dokument" ? "document" : normalizedKey;
          normalizedKey = normalizedKey === "baustoffe" ? "building materials" : normalizedKey;
          normalizedKey = normalizedKey === "elektrisch" ? "electrical" : normalizedKey;

          if (transportImages[normalizedKey]) {
            return transportImages[normalizedKey];
          }
        }
      }

      // 2. Try transport_type
      if (item?.transport_type) {
        const key = item.transport_type.toLowerCase();
        if (transportImages[key]) return transportImages[key];
      }

      if (item.transport?.transport_type) {
        const key = item.transport.transport_type.toLowerCase();
        if (transportImages[key]) return transportImages[key];
      }

      // 3. Try item type
      if (item?.type) {
        const key = item.type.toLowerCase();
        if (transportImages[key]) return transportImages[key];
      }

      return transportImages[type] || transportImages.other;
    };

    const imageSrc = getImageSrc();

    return (
      <img
        src={imageSrc}
        alt={type}
        className={className}
        style={{
          width: `${size}px`,
          // height: `${size}px`,
          objectFit: "contain",
          // filter: "invert(1)"
        }}
        onError={(e) => {
          e.target.src = transportImages.other;
        }}
      />
    );
  };

  const getTypeIcon = (type, item) => {
    return (
      <TransportImage
        type={type}
        item={item}
        className="text-secondary mb-2"
        size={35}
      />
    );
  };

  const getTypeText = (type, item) => {
    if (
      (type === "regular" || type === "fast") &&
      item.shipment_data?.length > 0
    ) {
      return item.shipment_data.map((s) => t(s.shipment_data) || s.shipment_data).join(", ");
    }
    switch (type) {
      case "fast":
        return `${t(item.type) || t("driverCard.general.item")} · ${item.quantity || 1
          }`;
      case "regular":
        return `${t(item.type) || t("driverCard.general.item")} · ${item.quantity || 1
          }`;
      case "furnitures":
        return t(item.transport_type) || t("driverCard.general.transport");
      case "cars":
        return t(item.item_type) || t("driverCard.general.transport");
      default:
        return t("driverCard.general.item");
    }
  };

  const getDateTime = (type, item, isPickup = true) => {
    if (type === "fast" || type === "regular") {
      if (isPickup) {
        // loading_date_time is an array like ["Di.14.04.2026 08:00 - 09:00"]
        const dateTimeArr = item.loading_date_time;
        const combined =
          Array.isArray(dateTimeArr) && dateTimeArr.length > 0
            ? dateTimeArr[0]
            : null;
        if (item.loading_date) {
          return { date: item.loading_date, time: item.loading_time || "" };
        }
        if (combined) {
          const spaceIdx = combined.indexOf(" ");
          const dtDate =
            spaceIdx > -1 ? combined.substring(0, spaceIdx) : combined;
          const dtTime =
            spaceIdx > -1
              ? combined.substring(spaceIdx + 1).replace(" - ", " → ")
              : "";
          return { date: dtDate, time: dtTime };
        }
        return { date: t("driverCard.general.notAvailable"), time: "" };
      } else {
        const dateTimeArr = item.unloading_date_time;
        const combined =
          Array.isArray(dateTimeArr) && dateTimeArr.length > 0
            ? dateTimeArr[0]
            : null;

        if (item.unloading_date) {
          return { date: item.unloading_date, time: item.unloading_time || "" };
        }
        if (combined) {
          const spaceIdx = combined.indexOf(" ");
          const dtDate =
            spaceIdx > -1 ? combined.substring(0, spaceIdx) : combined;
          const dtTime =
            spaceIdx > -1
              ? combined.substring(spaceIdx + 1).replace(" - ", " → ")
              : "";
          return { date: dtDate, time: dtTime };
        }
        return { date: t("driverCard.general.notAvailable"), time: "" };
      }
    } else {
      // furnitures / cars — التاريخ قادم كنص جاهز من الباك-إند
      if (isPickup) {
        const date =
          item.loading_date_start ||
          item.loading_date ||
          t("driverCard.general.notAvailable");
        const time =
          item.loading_time_start ||
          item.loading_time ||
          t("driverCard.general.notAvailable");
        return { date, time };
      } else {
        const date =
          item.unloading_date_start ||
          item.unloading_date ||
          t("driverCard.general.notAvailable");
        const time =
          item.unloading_time_start ||
          item.unloading_time ||
          t("driverCard.general.notAvailable");
        return { date, time };
      }
    }
  };

  const renderExpandedContent = (item, index) => {
    const fromCoords = parseCoordinates(item.booking_from_lat_long);
    const toCoords = parseCoordinates(item.booking_to_lat_long);

    // if (!fromCoords || !toCoords)
    //   return (
    //     <p className="text-danger text-center p-3">
    //       {t("driverCard.general.invalidCoords")}
    //     </p>
    //   );

    if (currentType === "fast" || currentType === "regular") {
      return (
        <MapErrorBoundary height="300px">
          <DarkBookingDetailsPage
            item={item}
            type={currentType}
            onClose={() =>
              setActiveKey((prev) => (prev === index.toString() ? null : prev))
            }
            driver={driver}
            setSelectedId={setSelectedId}
            setSelectedItemForMap={setSelectedItemForMap}
            setModalData={setModalData}
            setShowModal={setShowModal}
        />
        </MapErrorBoundary>
      );
    } else {
      // للنوع furnitures و cars
      return (
        <DarkTransportDetailsPage
          item={item}
          type={currentType}
          onSendOffer={(selectedItem) => {
            const rawPrice =
              selectedItem.discounted_price || selectedItem.price || "";
            const cleanPrice =
              typeof rawPrice === "string"
                ? rawPrice.replace(/\./g, "").replace(/,/g, ".")
                : rawPrice;

            setSelectedItemForMap(selectedItem);
            resetCalculator();
            setModalData({
              transport_id: selectedItem.id,
              driver_name: "",
              phone_number: "",
              vehicle_number: "",
              vehicle_type: "",
              offer_date: "",
              current_location: driver.postal_code || "",
              price: cleanPrice,
              driving_choice: "",
            });
            setShowModal(true);
          }}
          onChat={handleOpenChat}
          shouldShowChat={shouldShowChat(currentType)}
          showChat={true}
          onClose={() =>
            setActiveKey((prev) => (prev === index.toString() ? null : prev))
          }
        />
      );
    }
  };

  const renderPriceDetails = (item) => {
    if (currentType === "fast" || currentType === "regular") {
      return (
        <div className="d-flex flex-column align-items-center gap-2">
          <h5 className="fw-bold">
            {t("driverCard.priceDetails.priceSuggestion")}:{" "}
            {item.discounted_price || "0"} EUR (Netto)
          </h5>
        </div>
      );
    }
  };

  const renderPriceModalContent = () => {
    console.log("⛽priceStatus", priceStatus);
    if (currentType === "regular") {
      return (
        priceStatus && (
          priceStatus.percentage !== null && priceStatus.percentage !== undefined ? (
            <PriceBar percentage={priceStatus.percentage} customerPrice={priceStatus.customerPrice} t={t} />
          ) : (
            <p className="text-secondary fw-bold text-center mt-2">
              {t(priceStatus.text)}
            </p>
          )
        )
      );
    } else {
      return (
        resData && (
          <>
            <h5 className="fw-bold mb-3">
              {t("driverCard.priceDetails.priceDetails")}:
            </h5>
            <hr />
            <div className="text-start">
              <p>
                <strong>{t("driverCard.priceDetails.basePrice")}:</strong>{" "}
                {formatNumber(resData?.base_price) || "—"} €
              </p>
              <p>
                <strong>{t("driverCard.priceDetails.vat")}:</strong>{" "}
                {formatNumber(resData?.vat_amount) || "—"} €
              </p>
              <p>
                <strong>{t("driverCard.priceDetails.priceAfterVat")}:</strong>{" "}
                {formatNumber(resData?.price_after_vat) || "—"} €
              </p>
              <p>
                <strong>
                  {t("driverCard.priceDetails.platformCommission")}:
                </strong>{" "}
                {selectedTransportHasCommission
                  ? `${formatNumber(resData?.platform_commission) || "—"} €`
                  : commissionLabel}
              </p>
              <hr />
              <p className="fw-bold text-success">
                <strong>{t("driverCard.priceDetails.totalPrice")}:</strong>{" "}
                {resData?.total_price ?? "—"} €
              </p>
            </div>
            <p className="mt-3">
              {t("driverCard.priceDetails.priceCalculated")}
            </p>
          </>
        )
      );
    }
  };

  // تحديث renderModalFields لاستخدام select بدلاً من input
  const renderModalFields = () => {
    const handleDriverSelect = (driverName) => {
      const selectedDriver = driverVehicles.find(
        (vehicle) => vehicle.driver_name === driverName,
      );

      if (selectedDriver) {
        setModalData((prev) => ({
          ...prev,
          driver_name: selectedDriver.driver_name,
          phone_number: selectedDriver.phone_number,
          vehicle_number: selectedDriver.vehicle_number,
          vehicle_type: selectedDriver.vehicle_type,
          vehicle_id: selectedDriver.id,
        }));
      }
    };

    // التحقق مما إذا كان driving = 1 للسيارات
    const hasDrivingOption =
      currentType === "cars" &&
      selectedItemForMap?.driving?.some((drive) => drive === "1");
    const showVehicleNumberInput =
      !hasDrivingOption || modalData.driving_choice === "drive";

    // الحقول الأساسية المشتركة لجميع الأنواع
    const commonFields = (
      <>
        {/* الصف الأول: المعلومات الأساسية */}
        <Row className="mb-3">
          <Col md={6}>
            <Form.Group>
              <Form.Label className="fw-bold">
                {t("driverCard.modalFields.driverName")} *
              </Form.Label>
              <Form.Select
                value={modalData.driver_name}
                onChange={(e) => {
                  const selectedName = e.target.value;
                  setModalData((prev) => ({
                    ...prev,
                    driver_name: selectedName,
                  }));
                  handleDriverSelect(selectedName);
                }}
                className="form-control"
                required
              >
                <option value="">
                  {t("driverCard.modalFields.enterDriverName")}
                </option>
                {driverVehicles.map((vehicle) => (
                  <option key={vehicle.id} value={vehicle.driver_name}>
                    {vehicle.driver_name}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
          </Col>

          <Col md={6}>
            <Form.Group>
              <Form.Label className="fw-bold">
                {t("driverCard.modalFields.phoneNumber")} *
              </Form.Label>
              <Form.Select
                value={modalData.phone_number}
                onChange={(e) => {
                  const selectedPhone = e.target.value;
                  setModalData((prev) => ({
                    ...prev,
                    phone_number: selectedPhone,
                  }));
                }}
                className="form-control"
                required
              >
                <option value="">
                  {t("driverCard.modalFields.enterPhoneNumber")}
                </option>
                {driverVehicles.map((vehicle) => (
                  <option key={vehicle.id} value={vehicle.phone_number}>
                    {vehicle.phone_number}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
          </Col>
        </Row>

        {/* الصف الثاني: نوع المركبة ورقمها */}
        <Row className="mb-3">
          <Col md={6}>
            <Form.Group>
              <Form.Label className="fw-bold">
                {t("driverCard.modalFields.vehicleType")} *
              </Form.Label>
              <Form.Select
                value={modalData.vehicle_type}
                onChange={(e) => {
                  const selectedVehicleType = e.target.value;
                  setModalData((prev) => ({
                    ...prev,
                    vehicle_type: selectedVehicleType,
                  }));

                  if (selectedVehicleType && !modalData.driver_name) {
                    const selectedByType = driverVehicles.find(
                      (vehicle) => vehicle.vehicle_type === selectedVehicleType,
                    );
                    if (selectedByType) {
                      setModalData((prev) => ({
                        ...prev,
                        driver_name: selectedByType.driver_name,
                        phone_number: selectedByType.phone_number,
                        vehicle_number: selectedByType.vehicle_number,
                        vehicle_id: selectedByType.id,
                      }));
                    }
                  }
                }}
                className="form-control"
                required
              >
                <option value="">
                  {t("driverCard.modalFields.enterVehicleType")}
                </option>
                {driverVehicles.map((vehicle) => (
                  <option key={vehicle.id} value={vehicle.vehicle_type}>
                    {vehicle.vehicle_type}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
          </Col>

          {/* إظهار vehicle_number فقط إذا كان driving = 0 أو اختار drive */}
          {showVehicleNumberInput && (
            <Col md={6}>
              <Form.Group>
                <Form.Label className="fw-bold">
                  {t("driverCard.modalFields.vehicleNumber")} *
                </Form.Label>
                <Form.Select
                  value={modalData.vehicle_number || ""}
                  onChange={(e) => {
                    const selectedVehicleNumber = e.target.value;
                    setModalData((prev) => ({
                      ...prev,
                      vehicle_number: selectedVehicleNumber,
                    }));
                  }}
                  className="form-control"
                  required
                >
                  <option value="">
                    {t("driverCard.modalFields.enterVehicleNumber")}
                  </option>
                  {driverVehicles.map((vehicle) => (
                    <option key={vehicle.id} value={vehicle.vehicle_number}>
                      {vehicle.vehicle_number}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>
          )}
        </Row>

        {/* سؤال driving للسيارات */}
        {hasDrivingOption && (
          <Row className="mb-3">
            <Col md={12}>
              <Form.Group>
                <Form.Label className="fw-bold">
                  {t("driverCard.modalFields.drivingQuestion")} *
                </Form.Label>
                <div className="d-flex gap-4 mt-2">
                  <Form.Check
                    type="radio"
                    label={t("driverCard.modalFields.driveCar")}
                    name="driving_choice"
                    value="drive"
                    checked={modalData.driving_choice === "drive"}
                    onChange={(e) =>
                      setModalData((prev) => ({
                        ...prev,
                        driving_choice: e.target.value,
                      }))
                    }
                    className="fs-6"
                    required
                  />
                  <Form.Check
                    type="radio"
                    label={t("driverCard.modalFields.transportCar")}
                    name="driving_choice"
                    value="load"
                    checked={modalData.driving_choice === "load"}
                    onChange={(e) =>
                      setModalData((prev) => ({
                        ...prev,
                        driving_choice: e.target.value,
                      }))
                    }
                    className="fs-6"
                    required
                  />
                </div>
              </Form.Group>
            </Col>
          </Row>
        )}

        {/* الصف الثالث: الموقع */}
        <Row className="mb-3">
          <Col md={12}>
            <Form.Group>
              <Form.Label className="fw-bold">
                {t("driverCard.modalFields.location")} *
              </Form.Label>
              <LocationAutocomplete
                name="current_location"
                value={modalData.current_location || ""}
                onChange={handleModalLocationChange}
                onSelect={(suggestion) => {
                  if (suggestion && suggestion.label) {
                    setModalData((prev) => ({
                      ...prev,
                      current_location: suggestion.label,
                      current_location_lat: suggestion.lat,
                      current_location_lng: suggestion.lng,
                    }));
                  }
                }}
                placeholder={t("driverCard.modalFields.enterLocation")}
                shouldLoadOptions={true}
                className="form-control"
              />
            </Form.Group>
          </Col>
        </Row>
      </>
    );

    // الحقول الخاصة بكل نوع
    const specificFields = () => {
      console.log("⛽priceStatus", priceStatus);
      if (currentType === "regular") {
        return (
          <>
            {/* السعر لـ regular */}
            <Row className="mb-3">
              <Col md={12}>
                <Form.Group>
                  <Form.Label className="fw-bold">
                    {t("driverCard.modalFields.price")} (€) *
                  </Form.Label>
                  <Form.Control
                    type="number"
                    placeholder={t("driverCard.modalFields.enterOfferPrice")}
                    value={modalData.price || ""}
                    onChange={(e) => handlePriceChange(e, "modal")}
                    className="form-control"
                    required
                    min="0"
                    step="0.01"
                  />

                  {/* عرض نتيجة السعر لـ regular */}
                  {priceStatus && (
                    <div className="mt-3 p-3 border rounded bg-light">
                      {priceStatus.percentage !== null && priceStatus.percentage !== undefined ? (
                        <PriceBar percentage={priceStatus.percentage} customerPrice={priceStatus.customerPrice} t={t} />
                      ) : (
                        <p className={`text-center fw-bold mb-0 ${priceStatus.color}`}>
                          {t(priceStatus.text)}
                        </p>
                      )}
                    </div>
                  )}
                </Form.Group>
              </Col>
            </Row>
          </>
        );
      } else if (currentType === "furnitures" || currentType === "cars") {
        return (
          <>
            <Row className="mb-3">
              <Col md={6}>
                <Form.Group>
                  <Form.Label className="fw-bold">
                    {t("driverCard.modalFields.price")} (€) *
                  </Form.Label>
                  <Form.Control
                    type="number"
                    placeholder={t("driverCard.modalFields.enterOfferPrice")}
                    value={modalData.price || ""}
                    onChange={(e) => handlePriceChange(e, "modal")}
                    className="form-control"
                    required
                    min="0"
                    step="0.01"
                  />

                  {resData && (
                    <div className="mt-2 p-2 border rounded bg-light">
                      <div className="d-flex justify-content-between align-items-center">
                        <small className="dark-info-label">
                          <strong>
                            {t("driverCard.priceDetails.vat")} (
                            {resData.vat_rate}):
                          </strong>
                        </small>
                        <small className="text-dark fw-bold">
                          {/* {resData.vat_percentage} % */}
                          {resData.vat_count} €
                        </small>
                      </div>

                      {resData.base_price && (
                        <>
                          <div className="d-flex justify-content-between align-items-center mt-1">
                            <small className="dark-info-label">
                              <strong>
                                {t("driverCard.priceDetails.basePrice")}:
                              </strong>
                            </small>
                            <small className="text-dark">
                              {resData.price_after_vat} €
                            </small>
                          </div>
                          <div className="d-flex justify-content-between align-items-center mb-2">
                            <small className="dark-info-label">
                              <strong>
                                {t(
                                  "driverCard.priceDetails.platformCommission",
                                )}
                                :
                              </strong>
                            </small>
                            {selectedTransportHasCommission ? (
                              <span className="text-success fw-bold">
                                {resData.platform_commission} €
                                <button
                                  type="button"
                                  className="btn btn-secondary btn-sm ms-2"
                                  onClick={() => setShowModalQuestion(true)}
                                >
                                  <LuMessageCircleQuestion />
                                </button>
                                {showModalQuestion && (
                                  <div
                                    className="modal show d-block"
                                    tabIndex="-1"
                                  >
                                    <div className="modal-dialog modal-dialog-centered">
                                      <div className="modal-content bg-black-light">
                                        <div className="modal-header">
                                          <h5 className="modal-title">
                                            {t("driverCard.orderDetails.notes")}
                                          </h5>
                                          <button
                                            type="button"
                                            className="btn-close"
                                            onClick={() =>
                                              setShowModalQuestion(false)
                                            }
                                          ></button>
                                        </div>
                                        <div className="modal-body">
                                          <strong>
                                            {t(
                                              "driverCard.alerts.commissionText",
                                            )}
                                            : {resData?.platform_commission} €
                                          </strong>
                                        </div>
                                        <div className="modal-footer">
                                          <button
                                            type="button"
                                            className="btn btn-secondary"
                                            data-bs-dismiss="modal"
                                            onClick={() =>
                                              setShowModalQuestion(false)
                                            }
                                          >
                                            {t("driverCard.buttons.cancel")}
                                          </button>
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                )}
                              </span>
                            ) : (
                              <span className="dark-info-label fw-bold">
                                {commissionLabel}
                              </span>
                            )}
                          </div>
                        </>
                      )}
                    </div>
                  )}
                </Form.Group>
              </Col>

              <Col md={6}>
                <Form.Group>
                  <Form.Label className="fw-bold">
                    {t("driverCard.modalFields.offerDate")}
                  </Form.Label>
                  <Form.Control
                    type="date"
                    value={modalData.offer_date || ""}
                    min={new Date().toISOString().split("T")[0]}
                    onChange={(e) =>
                      setModalData((prev) => ({
                        ...prev,
                        offer_date: e.target.value || null,
                      }))
                    }
                    className="form-control"
                  />
                  <Form.Text className="dark-info-label">
                    {t("driverCard.modalFields.optionalDate")}
                  </Form.Text>
                </Form.Group>
              </Col>
            </Row>
          </>
        );
      }
      return null;
    };

    // الحقل info (دائماً آخر حقل)
    const infoField = (
      <Row className="mb-3">
        <Col md={12}>
          <Form.Group>
            <Form.Label className="fw-bold">
              {t("driverCard.modalFields.info")}
            </Form.Label>
            <Form.Control
              as="textarea"
              rows={4}
              placeholder={t("driverCard.modalFields.enterAdditionalInfo")}
              value={modalData.info || ""}
              onChange={(e) =>
                setModalData((prev) => ({ ...prev, info: e.target.value }))
              }
              className="form-control"
            />
          </Form.Group>
        </Col>
      </Row>
    );

    return (
      <>
        {commonFields}
        {specificFields()}
        {infoField}
      </>
    );
  };

  const toggleOffCanvas = (index) => {
    setOffCanvasOpen(offCanvasOpen === index ? null : index);
  };

  // مكون الفلاتر المشترك
  const renderFilterForm = () => (
    <Form>
      <div className="mb-4">
        <div className="d-flex align-items-center justify-content-between mb-2">
          <h6 className="mb-0">
            {t("driverCard.savedFilters.title", {
              defaultValue: "Saved filters",
            })}
          </h6>
          <Badge bg="secondary">
            {savedFiltersForDisplay.filter(
              (savedFilter) => savedFilter.filter_type === currentType,
            ).length}
          </Badge>
        </div>

        {savedFiltersLoading ? (
          <div className="dark-info-label small">
            {t("driverCard.savedFilters.loading", {
              defaultValue: "Loading saved filters...",
            })}
          </div>
        ) : savedFiltersForDisplay.length === 0 ? (
          <Alert variant="warning" className="border small mb-0">
            {t("driverCard.savedFilters.empty", {
              defaultValue: "No saved filters yet.",
            })}
          </Alert>
        ) : (
          <div className="d-flex flex-column gap-2">
            {savedFiltersForDisplay.map((savedFilter) => (
              <Card key={savedFilter.id} className="border-0 shadow-sm">
                <Card.Body className="py-2 px-3">
                  <div className="d-flex justify-content-between align-items-start gap-2">
                    <div>
                      <div className="fw-semibold d-flex align-items-center gap-2">
                        <FaRegBookmark className="text-warning" />
                        <span>{savedFilter.name}</span>
                      </div>
                      <div className="dark-info-label small mt-1">
                        {getSavedFilterTypeLabel(savedFilter.filter_type)}
                      </div>
                    </div>

                    <div className="d-flex gap-2">
                      <Button
                        size="sm"
                        variant="outline-primary"
                        onClick={() => handleApplySavedFilter(savedFilter)}
                      >
                        {t("driverCard.savedFilters.apply", {
                          defaultValue: "Apply",
                        })}
                      </Button>
                      <Button
                        size="sm"
                        variant="outline-danger"
                        onClick={() => handleDeleteSavedFilter(savedFilter)}
                      >
                        <FaTrash />
                      </Button>
                    </div>
                  </div>
                </Card.Body>
              </Card>
            ))}
          </div>
        )}
      </div>

      <Form.Group className="mb-3">
        <Form.Label>{t("driverCard.filters.from")}</Form.Label>
        <LocationAutocomplete
          name="booking_from"
          value={tempFilters.booking_from}
          onChange={handleTempLocationChange}
          placeholder={t("driverCard.filters.from")}
        />
      </Form.Group>

      {tempFilters.booking_from && (
        <Form.Group className="mb-3">
          <Form.Label>
            {t("driverCard.filters.fromRadius")} ({tempFilters.from_radius} km)
          </Form.Label>
          <Form.Range
            min={0}
            max={1000}
            step={1}
            name="from_radius"
            value={tempFilters.from_radius || 0}
            onChange={handleTempFilterChange}
          />
        </Form.Group>
      )}

      <Form.Group className="mb-3">
        <Form.Label>{t("driverCard.filters.to")}</Form.Label>
        <LocationAutocomplete
          name="booking_to"
          value={tempFilters.booking_to}
          onChange={handleTempLocationChange}
          placeholder={t("driverCard.filters.to")}
        />
      </Form.Group>

      {tempFilters.booking_to && (
        <Form.Group className="mb-3">
          <Form.Label>
            {t("driverCard.filters.toRadius")} ({tempFilters.to_radius} km)
          </Form.Label>
          <Form.Range
            min={0}
            max={1000}
            step={1}
            name="to_radius"
            value={tempFilters.to_radius || 0}
            onChange={handleTempFilterChange}
          />
        </Form.Group>
      )}

      <Form.Group className="mb-3">
        <Form.Label>{t("driverCard.filters.location")}</Form.Label>
        <LocationAutocomplete
          name="location"
          value={tempFilters.location}
          onChange={handleTempLocationChange}
          placeholder={t("driverCard.filters.location")}
        />
      </Form.Group>

      {tempFilters.location && (
        <Form.Group className="mb-3">
          <Form.Label>
            {t("driverCard.filters.radius")} ({tempFilters.radius} km)
          </Form.Label>
          <Form.Range
            min={0}
            max={1000}
            step={1}
            name="radius"
            value={tempFilters.radius || 0}
            onChange={handleTempFilterChange}
          />
        </Form.Group>
      )}

      <Form.Group className="mb-3">
        <Form.Label>{t("driverCard.filters.weight")}</Form.Label>
        <Form.Control
          placeholder={t("driverCard.filters.weight")}
          name="weight"
          value={tempFilters.weight}
          onChange={handleTempFilterChange}
        />
      </Form.Group>

      {(currentType === "fast" || currentType === "regular") && (
        <>
          <Form.Group className="mb-3">
            <Form.Label>{t("driverCard.filters.bookingType")}</Form.Label>
            <Form.Control
              placeholder={t("driverCard.filters.bookingType")}
              name="booking_type"
              value={tempFilters.booking_type}
              onChange={handleTempFilterChange}
            />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>{t("driverCard.filters.vehicleType")}</Form.Label>
            <Form.Select
              name="vehicle_type"
              onChange={handleVehicleChange}
              value={formData.vehicle_id}
            >
              <option value="">{t("driverCard.filters.selectVehicle")}</option>
              {vehicles.map((vehicle) => (
                <option key={vehicle.id} value={vehicle.id}>
                  🚚 {vehicle.name} — {vehicle.size}
                </option>
              ))}
            </Form.Select>
          </Form.Group>
        </>
      )}

      <Form.Group className="mb-3">
        <Form.Label>{t("driverCard.filters.date")}</Form.Label>
        <Form.Control
          type="date"
          name="date"
          value={tempFilters.date}
          onChange={handleTempFilterChange}
        />
      </Form.Group>

      <div className="d-grid gap-2">
        <Button className="bg-orange" onClick={handleApplyFilters}>
          {t("driverCard.buttons.apply")}
        </Button>
        <Button variant="outline-secondary" onClick={handleClearFilters}>
          {t("driverCard.buttons.clear")}
        </Button>
      </div>
    </Form>
  );

  return (
    <>
      <NavbarMenu />

      <Container
        fluid
        className="p-4"
        style={{ minHeight: "100vh" }}
      >
        {/* 🔹 Tabs */}
        <Row className="text-center mb-4">
          <Col>
            <div className="professional-tabs">
              <Button
                className={`tab-btn ${currentType === "fast" ? "bg-dark active" : "bg-orange"
                  }`}
                onClick={() => handleTypeChange("fast")}
              >
                <FaRocket className="me-2" />
                {t("driverCard.orderTypes.fast")}

                <span className="ms-2 badge bg-orange-50 rounded-circle">
                  {countData.fast_bookings}
                </span>
              </Button>
              <Button
                className={`tab-btn ${currentType === "regular" ? "bg-dark active" : "bg-orange"
                  }`}
                onClick={() => handleTypeChange("regular")}
              >
                <FaBox className="me-2" />
                {t("driverCard.orderTypes.regular")}

                <span className="ms-2 badge bg-orange-50 rounded-circle">
                  {countData.bookings}
                </span>
              </Button>
              <Button
                className={`tab-btn ${currentType === "furnitures" ? "bg-dark active" : "bg-orange"
                  }`}
                onClick={() => handleTypeChange("furnitures")}
              >
                <FaCouch className="me-2" />
                {t("driverCard.orderTypes.furniture")}

                <span className="ms-2 badge bg-orange-50 rounded-circle">
                  {countData.furniture_transports}
                </span>
              </Button>
              <Button
                className={`tab-btn ${currentType === "cars" ? "bg-dark active" : "bg-orange"
                  }`}
                onClick={() => handleTypeChange("cars")}
              >
                <FaCar className="me-2" />
                {t("driverCard.orderTypes.car")}

                <span className="ms-2 badge bg-orange-50 rounded-circle">
                  {countData.car_transports}
                </span>
              </Button>
            </div>
          </Col>
        </Row>

        {/* filters btn */}
        <Row className={!isApprovedByType ? "approval-blur" : ""}>
          <Col
            xs={12}
            md={6}
            className="d-flex align-items-center mb-2 mb-md-0"
          >
            <div className="d-flex justify-content-between align-items-center mb-4">
              <h4 className="mb-0">
                {t("driverCard.filters.filter")}
              </h4>
              <Button
                className="bg-orange mx-2"
                onClick={handleOpenFilterDrawer}
              >
                <FaFilter className="me-2" />
                {t("driverCard.filters.button")}
              </Button>
            </div>

            {/* 🔹 دراور الفلاتر المشترك */}
            <Offcanvas
              show={showFilterDrawer}
              onHide={handleCloseFilterDrawer}
              placement="end"
              style={{ zIndex: "99999999" }}
              // className="bg-dark text-white"
            >
              <Offcanvas.Header closeButton>
                <Offcanvas.Title>
                  {t("driverCard.filters.filter")}
                </Offcanvas.Title>
              </Offcanvas.Header>
              <Offcanvas.Body>{renderFilterForm()}</Offcanvas.Body>
            </Offcanvas>
          </Col>
        </Row>

        {profileLoading ? (
          <Row className="justify-content-center">
            <Col xs={12} className="text-center">
              <Spinner animation="border" variant="primary" />
              <p className="mt-2">{t("driverCard.general.loading")}</p>
            </Col>
          </Row>
        ) : driver && driver?.admin_approve === "2" ? (
          <Row>
            <Col lg={12} className="ps-0 pe-0">
              <div className="approval-overlay-container">
                {!isApprovedByType && (
                  <div className="approval-overlay">
                    <div className="approval-overlay-message text-center">
                      <FaExclamationTriangle className="overlay-icon" />
                      {t("driverCard.alerts.notApprovedForType")}
                    </div>
                  </div>
                )}
                <div className={!isApprovedByType ? "approval-blur" : ""}>
                  <Accordion
                    activeKey={activeKey}
                    className="d-none d-md-block dark-accordion-container"
                  >
                    {loading && (
                      <p className="dark-text-muted">
                        {t("driverCard.general.loading")}
                      </p>
                    )}
                    {!loading &&
                      (!Array.isArray(data) || data.length === 0) && (
                        <p className="text-center dark-text-muted py-5">
                          {t("driverCard.general.noData")}
                        </p>
                      )}
                    {!loading &&
                      Array.isArray(data) &&
                      data.map((item, index) => {
                        const fromCoords = parseCoordinates(
                          item.booking_from_lat_long,
                        );
                        const toCoords = parseCoordinates(
                          item.booking_to_lat_long,
                        );
                        const pickupDateTime = getDateTime(
                          currentType,
                          item,
                          true,
                        );
                        const deliveryDateTime = getDateTime(
                          currentType,
                          item,
                          false,
                        );

                        return (
                          <Accordion.Item
                            eventKey={index.toString()}
                            key={index}
                          >
                            <Accordion.Header
                              onClick={() => {
                                const newActiveKey =
                                  activeKey === index.toString()
                                    ? null
                                    : index.toString();
                                setActiveKey(newActiveKey);
                              }}
                            >
                              <div className="w-100 p-2">
                                {/* Header Top: Vehicle */}
                                {/* <div
                                  className="d-flex align-items-center mb-3 pb-3"
                                  style={{ borderBottom: "1px solid rgb(212 212 212)" }}
                                >
                                  <TransportImage
                                    type={currentType}
                                    item={item}
                                    size={32}
                                  />
                                  <h6 className="mb-0 ms-3 text-dark fw-bold text-capitalize">
                                    {getTypeText(currentType, item)}
                                  </h6>
                                  <div className="ms-auto d-flex align-items-center gap-2">
                                    <Badge
                                      bg="secondary"
                                      className="text-white px-3 py-1"
                                      style={{ borderRadius: "20px", fontSize: "0.8rem", backgroundColor: "#333" }}
                                    >
                                      {Array.isArray(item.booking_from) ? item.booking_from.length : 1}{" "}
                                      {t("driverCard.general.stopsCount", "Stops")}
                                    </Badge>
                                  </div>
                                </div> */}

                                {/* Middle Content */}
                                <Row className="align-items-center g-2 px-2 py-0 justify-content-between">
                                  {/* Transport Image */}
                                  <Col xs={12} md={2} className="text-center">
                                    <TransportImage
                                      type={currentType}
                                      item={item}
                                      // size={50}
                                      width={180}
                                    />

                                    <div className="mb-1 d-flex align-items-center text-dark fw-bold justify-content-center">
                                      <span>{getTypeText(currentType, item)}</span>
                                    </div>
                                    
                                  </Col>

                                  {/* Pickup */}
                                  <Col xs={12} md={2}>
                                    <h6 className="text-orange fw-bold mb-2 small text-uppercase">
                                      {t(
                                        "driverCard.general.pickupTitle",
                                        "ABHOLUNG (BELADUNG)",
                                      )}
                                    </h6>
                                    {(Array.isArray(item.booking_from)
                                      // ? item.booking_from
                                      // : [item.booking_from].filter(Boolean)
                                      ? [item.booking_from[0]]
                                      : [item.booking_from].filter(Boolean)
                                    ).map((addr, i) => {
                                      // const rawDt = item.loading_date_start
                                      //   ? null
                                      //   : (Array.isArray(item.loading_date_time)
                                      //     ? item.loading_date_time[i]
                                      //     : null) || "";

                                      const rawDt = item.loading_date_start
                                        ? null
                                        : (Array.isArray(item.loading_date_time)
                                          ? item.loading_date_time[0]
                                          : null) || "";
                                      const spaceIdx = rawDt
                                        ? rawDt.indexOf(" ")
                                        : -1;
                                      const dtDate =
                                        item.loading_date_start ||
                                        item.loading_date ||
                                        (spaceIdx > -1
                                          ? rawDt.substring(0, spaceIdx)
                                          : rawDt) ||
                                        "-";
                                      const dtTime =
                                        item.loading_time_start ||
                                        item.loading_time ||
                                        (spaceIdx > -1
                                          ? rawDt
                                            .substring(spaceIdx + 1)
                                            .replace(" - ", " → ")
                                          : "-");
                                      return (
                                        <div key={i} className="mb-2">
                                          <div className="fw-bold text-orange mb-1 small">
                                            {t("driverCard.general.pickup")} ({i + 1})
                                          </div>
                                          <div className="mb-1 d-flex align-items-center text-dark fw-bold">
                                            <FaMapMarkerAlt className="text-orange me-2 fs-5 flex-shrink-0" />
                                            <span>{addr}</span>
                                          </div>
                                          <div
                                            className="p-2 d-inline-block"
                                            style={{
                                              backgroundColor: "inherit",
                                              borderRadius: "8px",
                                              border: "1px solid rgb(212 212 212 / 39%)",
                                            }}
                                          >
                                            <div className="d-flex align-items-center mb-1 gap-3">
                                              <span
                                                className="d-flex align-items-center dark-info-label small"
                                                style={{ minWidth: "70px" }}
                                              >
                                                <FaCalendarAlt className="text-orange me-2" />{" "}
                                                {t(
                                                  "driverCard.general.dates",
                                                  "Datum",
                                                )}
                                              </span>
                                              <span className="text-dark fw-medium small flex-grow-1 text-start">
                                                {dtDate}
                                                {item.loading_date_end
                                                  ? ` → ${item.loading_date_end}`
                                                  : ""}
                                              </span>
                                            </div>
                                            <div
                                              className="d-flex align-items-center mt-1 pt-1 gap-3"
                                              style={{
                                                borderTop: "1px solid rgb(212 212 212)",
                                              }}
                                            >
                                              <span
                                                className="d-flex align-items-center dark-info-label small"
                                                style={{ minWidth: "70px" }}
                                              >
                                                <AlarmIcon
                                                  className="text-orange me-2"
                                                  style={{ width: 16 }}
                                                />{" "}

                                                <span className="dark-info-label" style={{ marginLeft: "4px" }}>
                                                  {t(
                                                    "driverCard.general.time",
                                                    "Uhrzeit",
                                                  )}
                                                </span>
                                              </span>
                                              <span className="text-dark fw-medium small flex-grow-1 text-start d-flex align-items-center">
                                                {dtTime}
                                                {item.loading_time_end
                                                  ? ` → ${item.loading_time_end}`
                                                  : ""}
                                              </span>
                                            </div>
                                          </div>
                                        </div>
                                      );
                                    })}
                                  </Col>

                                  {/* Arrow */}
                                  <Col
                                    xs={12}
                                    md={1}
                                    className="text-start d-none d-md-flex p-0"
                                    style={{
                                      position: "relative",
                                      minHeight: "100px",
                                    }}
                                  >
                                    <div
                                      className="d-flex align-items-center justify-content-start"
                                      style={{
                                        position: "absolute",
                                        top: "70%",
                                        left: "0%",
                                        transform: "translate(0%, -50%)",
                                        minWidth: "80px",
                                        width: "60%",
                                        zIndex: 1,
                                      }}
                                    >
                                      <div
                                        style={{
                                          height: "1px",
                                          backgroundColor: "#6c757d",
                                          flex: 1,
                                        }}
                                      ></div>
                                      <div
                                        style={{
                                          width: "0",
                                          height: "0",
                                          borderTop: "4px solid transparent",
                                          borderBottom: "4px solid transparent",
                                          borderLeft: "6px solid #6c757d",
                                        }}
                                      ></div>
                                    </div>
                                  </Col>

                                  {/* Delivery */}
                                  <Col xs={12} md={2}>
                                    <h6 className="text-orange fw-bold mb-2 small text-uppercase">
                                      {t(
                                        "driverCard.general.deliveryTitle",
                                        "ZUSTELLUNG (ENTLADUNG)",
                                      )}
                                    </h6>
                                    {(Array.isArray(item.booking_to)
                                      // ? item.booking_to
                                      // : [item.booking_to].filter(Boolean)
                                      ? [item.booking_to[0]]
                                      : [item.booking_to].filter(Boolean)
                                    ).map((addr, i) => {
                                      const rawDt = item.unloading_date_start
                                        ? null
                                        : (Array.isArray(
                                          item.unloading_date_time,
                                        )
                                          ? item.unloading_date_time[0]
                                          : null) || "";
                                      const spaceIdx = rawDt
                                        ? rawDt.indexOf(" ")
                                        : -1;
                                      const dtDate =
                                        item.unloading_date_start ||
                                        item.unloading_date ||
                                        (spaceIdx > -1
                                          ? rawDt.substring(0, spaceIdx)
                                          : rawDt) ||
                                        "-";
                                      const dtTime =
                                        item.unloading_time_start ||
                                        item.unloading_time ||
                                        (spaceIdx > -1
                                          ? rawDt
                                            .substring(spaceIdx + 1)
                                            .replace(" - ", " → ")
                                          : "-");
                                      return (
                                        <div key={i} className="mb-2">
                                          <div className="fw-bold text-orange mb-1 small">
                                            {t("driverCard.general.delivery")} ({i + 1})
                                          </div>
                                          <div className="mb-1 d-flex align-items-center text-dark fw-bold">
                                            <FaMapMarkerAlt className="text-secondary me-2 fs-5 flex-shrink-0" />
                                            <span>{addr}</span>
                                          </div>
                                          <div
                                            className="p-2 d-inline-block"
                                            style={{
                                              backgroundColor: "inherit",
                                              borderRadius: "8px",
                                              border: "1px solid rgb(212 212 212 / 39%)",
                                            }}
                                          >
                                            <div className="d-flex align-items-center mb-1 gap-3">
                                              <span
                                                className="d-flex align-items-center dark-info-label small"
                                                style={{ minWidth: "70px" }}
                                              >
                                                <FaCalendarAlt className="text-orange me-2" />{" "}
                                                {t(
                                                  "driverCard.general.dates",
                                                  "Datum",
                                                )}
                                              </span>
                                              <span className="text-dark fw-medium small flex-grow-1 text-start">
                                                {dtDate}
                                                {item.unloading_date_end
                                                  ? ` → ${item.unloading_date_end}`
                                                  : ""}
                                              </span>
                                            </div>
                                            <div
                                              className="d-flex align-items-center mt-1 pt-1 gap-3"
                                              style={{
                                                borderTop: "1px solid rgb(212 212 212)",
                                              }}
                                            >
                                              <span
                                                className="d-flex align-items-center dark-info-label small"
                                                style={{ minWidth: "70px" }}
                                              >
                                                <AlarmIcon
                                                  className="text-orange me-2"
                                                  style={{ width: 16 }}
                                                />{" "}

                                                <span className="dark-info-label" style={{ marginLeft: "4px" }}>
                                                  {t(
                                                    "driverCard.general.time",
                                                    "Uhrzeit",
                                                  )}
                                                </span>
                                              </span>
                                              <span className="text-dark fw-medium small flex-grow-1 text-start d-flex align-items-center">
                                                {dtTime}
                                                {item.unloading_time_end
                                                  ? ` → ${item.unloading_time_end}`
                                                  : ""}
                                              </span>
                                            </div>
                                          </div>
                                        </div>
                                      );
                                    })}
                                  </Col>

                                  {/* Stats */}
                                  <Col xs={12} md={2} className="text-center">
                                    <div
                                      className="ps-md-2"
                                      style={{ borderLeft: "1px solid rgb(212 212 212)", borderRight: "1px solid rgb(212 212 212)" }}
                                    >
                                      <div
                                        className="mb-2 pb-2"
                                        style={{
                                          borderBottom: "1px solid rgb(212 212 212)",
                                        }}
                                      >
                                        <div className="dark-info-label d-flex align-items-center justify-content-center mb-1">
                                          <FaRoute className="me-2 text-secondary" />{" "}
                                          {t(
                                            "driverCard.general.route",
                                            "Route",
                                          )}
                                        </div>
                                        <div className="text-dark fw-bold fs-5">
                                          {(item.distance || "0 km").replace("km", " km")}
                                        </div>
                                      </div>
                                      <div>
                                        <div className="dark-info-label d-flex align-items-center justify-content-center mb-1">
                                          <FaWeightHanging className="me-2 text-secondary" />{" "}
                                          {t(
                                            "driverCard.general.totalWeight",
                                            "Gewicht:",
                                          )}
                                        </div>
                                        <div className="text-dark fw-bold fs-5">
                                          {item.total_weight || 0} Kg
                                        </div>
                                      </div>
                                    </div>
                                  </Col>

                                  {/* price */}
                                  <Col xs={12} md={2} className="text-center">
                                    <span className="card-price text-success">{item.discounted_price ? `${item.discounted_price} €` : ""}</span>

                                    {item?.price_offer && (
                                      <>
                                        <span className="card-price text-success">
                                          {item.price_offer} €
                                        </span>
              
                                        {item?.is_price_negotiable !== 0 && (
                                          <div className="info-item mb-2 justify-content-center">
                                            <span className="label text-center">
                                              {t("delivery_summary.priceOffer.negotiable")}
                                            </span>
                                          </div>
                                        )}                     
                                      </>
                                    )}

                                    <div className="mb-1 d-flex align-items-center text-dark fw-bold justify-content-center">
                                      <div
                                        className={`${activeKey === index.toString() ? "open" : ""} py-2`}
                                      >
                                        <button className="btn-orange">{t("driverCard.general.showDetails")} →</button>
                                      </div>
                                    </div>
                                    
                                  </Col>
                                </Row>

                                {/* <div
                                  className="d-flex justify-content-end mt-3 pt-2 pe-3"
                                  // style={{ borderTop: "1px solid rgb(212 212 212)" }}
                                >
                                  <div
                                    className={`dark-artikel-collapse-icon ${activeKey === index.toString() ? "open" : ""}`}
                                  >
                                    <FaChevronDown
                                      style={{ color: "#ff7b00" }}
                                    />
                                  </div>
                                </div> */}
                              </div>
                            </Accordion.Header>

                            <Accordion.Body>
                              <div
                                // className="dark-bg-darker"
                                style={{
                                  borderRadius: "12px",
                                  padding: "1rem",
                                }}
                              >
                                {renderExpandedContent(item, index)}
                              </div>
                            </Accordion.Body>
                          </Accordion.Item>
                        );
                      })}
                  </Accordion>

                  {/* 🔹 Mobile View */}
                  <div className="d-block d-md-none">
                    {loading && (
                      <div className="text-center mt-3">
                        <Spinner animation="border" variant="warning" />
                        <p className="mt-2 dark-info-label">
                          {t("driverCard.general.loading")}
                        </p>
                      </div>
                    )}

                    {!loading &&
                      (!Array.isArray(data) || data.length === 0) && (
                        <p className="text-center dark-text-muted mt-4">
                          {t("driverCard.general.noData")}
                        </p>
                      )}

                    {!loading &&
                      Array.isArray(data) &&
                      data.map((item, index) => renderMobileCard(item, index))}
                  </div>
                </div>
              </div>

              {/* 🔹 Off Canvas Component */}
              {!loading &&
                Array.isArray(data) &&
                data.map((item, index) => {
                  const fromCoords = parseCoordinates(
                    item.booking_from_lat_long,
                  );
                  const toCoords = parseCoordinates(item.booking_to_lat_long);
                  const route = createRoute(fromCoords, toCoords);

                  return (
                    <div key={index}>
                      {/* Overlay */}
                      <div
                        className={`offcanvas-overlay ${offCanvasOpen === index ? "show" : ""
                          }`}
                        onClick={() => setOffCanvasOpen(null)}
                      />

                      {/* Off Canvas Body */}
                      <div
                        className={`offcanvas-accordion-body ${offCanvasOpen === index ? "open" : ""
                          }`}
                      >
                        <button
                          className="offcanvas-close-btn"
                          onClick={() => setOffCanvasOpen(null)}
                        >
                          <FaTimes />
                        </button>

                        <Card className="offcanvas-card">
                          <Card.Body>
                            <Row>
                              <Col xs={12}>
                                <div className="mb-5 g-2">
                                  {renderExpandedContent(item)}
                                </div>
                              </Col>
                            </Row>
                          </Card.Body>
                        </Card>
                      </div>
                    </div>
                  );
                })}

              <DriverChatModal
                show={showChatModal}
                handleClose={() => setShowChatModal(false)}
                transportId={selectedTransport?.transportId}
                orderId={selectedTransport?.orderId}
              />

              {pagination.total > 0 && (
                <PaginationComponent
                  currentPage={pagination.current_page}
                  lastPage={pagination.last_page}
                  onPageChange={handleFetch}
                  disabled={loading}
                />
              )}

              {/* 🔹 Modal: Submit Offer */}
              <Modal
                show={showModal}
                onHide={() => setShowModal(false)}
                centered
                size="lg"
              >
                <Modal.Header closeButton>
                  <Modal.Title>
                    {currentType === "furnitures" || currentType === "cars"
                      ? t("driverCard.modals.transportOfferTitle")
                      : t("driverCard.modals.submitOfferTitle")}
                  </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                  <Form>
                    <Form.Control
                      type="hidden"
                      value={
                        currentType === "furnitures" || currentType === "cars"
                          ? modalData.transport_id
                          : modalData.booking_id
                      }
                    />

                    {renderModalFields()}

                    <div className="text-center mt-3 d-flex justify-content-center gap-2">
                      <Button variant="secondary" onClick={handleCloseModal}>
                        {t("driverCard.buttons.cancel")}
                      </Button>
                      {/* <Button className="bg-orange px-4" onClick={handleSendOffer}>
                        {t('driverCard.buttons.sendOffer')}
                      </Button> */}
                      <Button
                        className="bg-orange px-4"
                        onClick={handleSendOffer}
                        disabled={isSubmitting}
                        style={{
                          minWidth: "120px",
                          position: "relative",
                        }}
                      >
                        {isSubmitting ? (
                          <>
                            <Spinner
                              as="span"
                              animation="border"
                              size="sm"
                              role="status"
                              aria-hidden="true"
                              className="me-2"
                            />
                            {t("driverCard.buttons.sending")}
                          </>
                        ) : (
                          <>
                            <FaPaperPlane className="me-2" />
                            {currentType === "fast"
                              ? t("driverCard.buttons.sendOffer")
                              : t("driverCard.buttons.sendOfferOrder")
                            }
                            
                          </>
                        )}
                      </Button>
                    </div>
                  </Form>
                </Modal.Body>
              </Modal>

              {/* 🔹 Price Modal */}
              <Modal
                show={showPriceModal}
                onHide={() => setShowPriceModal(false)}
                centered
              >
                <Modal.Body className="text-center p-4">
                  <Form.Group className="mb-3">
                    <Form.Label>
                      {t("driverCard.modals.enterYourPrice")}
                    </Form.Label>
                    <Form.Control
                      type="number"
                      value={price}
                      onChange={handlePriceChange}
                      placeholder={t("driverCard.modals.pricePlaceholder")}
                    />
                  </Form.Group>

                  {loading ? (
                    <div className="d-flex flex-column align-items-center">
                      <Spinner animation="border" variant="warning" />
                      <p className="mt-2 mb-0">
                        {currentType === "regular"
                          ? t("driverCard.modals.evaluationInProgress")
                          : t("driverCard.modals.calculationInProgress")}
                      </p>
                    </div>
                  ) : (
                    renderPriceModalContent()
                  )}

                  <Button
                    className="bg-orange mt-3"
                    onClick={() => setShowPriceModal(false)}
                  >
                    {t("driverCard.buttons.ok")}
                  </Button>
                </Modal.Body>
              </Modal>

              {/* 🔹 Success Modal */}
              <Modal
                show={showSuccess}
                onHide={() => setShowSuccess(false)}
                centered
                backdrop="static"
              >
                <Modal.Body className="text-center p-4">
                  <FaCheckCircle size={60} className="text-success mb-3" />
                  <h5 className="fw-bold mb-3">
                    {currentType === "furnitures" || currentType === "cars"
                      ? t("driverCard.alerts.transportOfferSuccess")
                      : t("driverCard.alerts.offerSentSuccess")}
                  </h5>
                  <Button
                    variant="success"
                    onClick={() => {
                      setShowSuccess(false);
                    }}
                  >
                    {t("driverCard.buttons.ok")}
                  </Button>
                </Modal.Body>
              </Modal>
            </Col>
          </Row>
        ) : (
          <div className="alert alert-warning" role="alert">
            <b>{t("driverCard.accountActivation.waitingTitle")}</b>
            <br />
            <br />
            <div>
              {t("driverCard.accountActivation.activationSteps")}
              <ul>
                <li>{t("driverCard.accountActivation.step1")}</li>
                <li>{t("driverCard.accountActivation.step2")}</li>
                <li>
                  {t("driverCard.accountActivation.step3")}
                  <ul>
                    <li>{t("driverCard.accountActivation.doc1")}</li>
                    <li>{t("driverCard.accountActivation.doc2")}</li>
                  </ul>
                </li>
                <li>{t("driverCard.accountActivation.step4")}</li>
              </ul>

              <br />
              {t("driverCard.accountActivation.vehicleSteps")}
              <ul>
                <li>{t("driverCard.accountActivation.vehicleStep1")}</li>
                <li>{t("driverCard.accountActivation.vehicleStep2")}</li>
                <li>{t("driverCard.accountActivation.vehicleStep3")}</li>
              </ul>

              <br />
              {t("driverCard.accountActivation.finalStep")}

              <br />
              <br />
              <b>{t("driverCard.accountActivation.notification")}</b>
            </div>
          </div>
        )}
      </Container>

      {(userType === "client" || userType === "driver" || userType === "driver_company") && (
        <ClientChatBox userType={userType} />
      )}
    </>
  );
};

export default DriverCard;